import React, { useState, useEffect, useRef, useCallback, createElement, version } from 'react';
import { View, Text, StyleSheet, TextInput, Pressable, Icon, Icon3 } from '../lib/react-native-compat';
import { updateUserDataArray as _updateUserDataArray, loadUserData as _loadUserData, loadHEETData as _loadHEETData, f, fNum, pNum, sum, cN, effectiveIsProjectCoordinator, effectiveIsBuyer, recordAudit } from '../db/company';
import { selectedJobAtom, jobUserDataAtom, jobCostDetailAtom, jobDataAtom, selectedJobDataAtom, selectedVersionAtom, projectCompleteAtom, startDateActualAtom, dataSourceVersionAtom, isLiveVersionAtom, jobPhaseCodesAtom, selectedVersionLockedAtom, userNameAtom, userIdAtom, jobHEETDataAtom, userEmailCheckAtom, userRoleAtom, previewRoleAtom } from '../store/company';
import { useApiTracker } from '../store/useApiStatus';
import { useAtom } from 'jotai';
import moment from 'moment';
import { NumberInput } from './numberInput';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { s, colors, headerCell, valueCell, valueCellAlt, pastWeekCell, weekDateHeader, stickyHeader, phaseCard, expandButton, sectionDivider, phaseCollapseHeader, editIndicator } from './burnToolPhasedStyles';
import { ManpowerPlanning } from './manpowerPlanning';
import AuditHistoryPanel from './auditHistoryPanel';
import { parseBurnToolPhasedWorkbook, diffBurnToolPhased, buildBurnToolPhasedRows, BurnToolPhasedUploadPreview } from './burnToolPhasedUpload';
import { buildBurnToolPhasedWorkbook, writeBurnToolPhasedXlsx, etcFromWeek, planHoursPerWeek } from './burnToolPhasedExport';
import { standardDisciplineFor } from '../db/wbsStandard';
import { voltaGridWbsFor } from '../db/wbsVoltaGrid';
import { isVoltaGridJob } from '../helpers/voltaGrid';

const DateInput = ({ value, onValueChange, onBlur, style }) => {
    const [date, setDate] = useState(value);
    const pendingRef = useRef(null);
    const timerRef = useRef(null);

    const flush = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (pendingRef.current !== null) {
            const v = pendingRef.current;
            pendingRef.current = null;
            onValueChange && onValueChange(v);
            return true;
        }
        return false;
    };

    return createElement('input', {
        type: 'date',
        value: date instanceof Date && date != 'Invalid Date' ? date?.toISOString().split("T")[0] : date,
        onChange: (event) => {
            // Update displayed value immediately, but defer the parent
            // commit (setEndDate + saveValue) until the user stops scrubbing
            // through months. Native <input type="date"> fires `change` for
            // every arrow-key step in the calendar popup; without this debounce
            // each step triggered a full re-render + save.
            const v = event.target.value
            setDate(v ? new Date(v) : v)
            pendingRef.current = v
            if (timerRef.current) clearTimeout(timerRef.current)
            timerRef.current = setTimeout(() => { flush() }, 400)
        },
        onBlur: (event) => {
            // On blur, flush any pending change immediately. If nothing was
            // pending, fall back to the legacy onBlur callback so callers that
            // re-save on blur still fire.
            const flushed = flush()
            if (!flushed) {
                onBlur && onBlur(event.target.value)
            }
        },
        style: style
    })
}

// Labor classification mapping (same as laborClassification.js)
export const LaborInterface = {
    "Assistant Division Manager": "MANAGEMENT",
    "Assistant Project Manager": "MANAGEMENT",
    "Assistant Superintendent": "SUPERINTENDENT",
    "Assistant Superintendent (S)": "SUPERINTENDENT",
    "Bending Engineer": "ENGINEER",
    "Bid Controls Specialist": "ESTIMATION",
    "Blaster": "CRAFT",
    "Business Development Director": "BD",
    "Carpenter": "CRAFT",
    "Civil Designer": "ENGINEER",
    "Civil General Superintendent": "SUPERINTENDENT",
    "Clerk": "ADMIN",
    "Common Labor": "CRAFT",
    "Construction Manager": "CM",
    "Craft Helper": "CRAFT",
    "Craftsman": "CRAFT",
    "Crane Operator": "CRAFT",
    "Designer": "ENGINEER",
    "Director": "MANAGEMENT",
    "Division Manager": "MANAGEMENT",
    "Document Controls Specialist": "ADMIN",
    "Draftsman": "ENGINEER",
    "Driller": "CRAFT",
    "EHS Specialist": "HSE",
    "EPC Project Specialist": "ADMIN",
    "Electrical Asst Superintendent": "SUPERINTENDENT",
    "Electrical Engineer": "ENGINEER",
    "Electrical Foreman": "FOREMAN",
    "Electrical General Foreman": "FOREMAN",
    "Electrical Journeyman 1": "CRAFT",
    "Electrical Journeyman 2": "CRAFT",
    "Electrical Superintendent": "SUPERINTENDENT",
    "Engineering Manager": "ENGINEER",
    "Engineering Specialist": "ENGINEER",
    "Equipment Operator": "CRAFT",
    "Estimating Manager": "ESTIMATION",
    "Executive Assistant": "ADMIN",
    "Fiber Division Administrator": "ADMIN",
    "Fiber Foreman": "FOREMAN",
    "Fleet Logistic Manager": "MANAGEMENT",
    "Fleet Manager": "MANAGEMENT",
    "Foreman": "FOREMAN",
    "Foreman (Hourly)": "FOREMAN",
    "Foreman (Salary)": "FOREMAN",
    "Foreman - Welding (w/o Rig)": "FOREMAN",
    "General Foreman": "FOREMAN",
    "General Foreman (Hourly)": "FOREMAN",
    "General Foreman (Salary)": "FOREMAN",
    "General Manager": "CM",
    "General Superintendent": "SUPERINTENDENT",
    "General Superintendent (H)": "SUPERINTENDENT",
    "General Superintendent (S)": "SUPERINTENDENT",
    "HSE & OQ Specialist": "HSE",
    "HSE & OQ Specialist 1": "HSE",
    "HSE & OQ Specialist 2": "HSE",
    "HSE Administrator": "HSE",
    "HSE Assistant": "HSE",
    "HSE Manager": "HSE",
    "HSE Specialist": "HSE",
    "HSE Specialist 1": "HSE",
    "HSE Specialist 2": "HSE",
    "HSE Specialist 3": "HSE",
    "HSE Trainer": "HSE",
    "I&C Commissioning Technician": "CRAFT",
    "I&E Construction Manager": "CM",
    "I&E Designer": "ENGINEER",
    "I&E Foreman": "FOREMAN",
    "I&E Project Manager": "PM",
    "I&E Superintendent": "SUPERINTENDENT",
    "IE Apprentice (1st Year)": "CRAFT",
    "IE Apprentice (2nd Year)": "CRAFT",
    "IE Apprentice (3rd Year)": "CRAFT",
    "IE Apprentice (4th Year)": "CRAFT",
    "IE Apprentice (5th Year)": "CRAFT",
    "Instrument Fitter": "CRAFT",
    "Instrument Technician": "CRAFT",
    "Intern": "CRAFT",
    "Journeyman": "CRAFT",
    "Journeymen Electrician": "CRAFT",
    "Junior Surveyor": "CRAFT",
    "Lead I&C Automation Engineer": "ENGINEER",
    "Lead Instrumentation Engineer": "ENGINEER",
    "Lead Mechanical Drafter": "ENGINEER",
    "Lead Structural Engineer": "ENGINEER",
    "Licensed Operator": "CRAFT",
    "Line Locator": "CRAFT",
    "Logistic Manager": "LOGISTICS",
    "Manager": "ADMIN",
    "Material Buyer/Expeditor": "PROCUREMENT",
    "Mechanic": "CRAFT",
    "Mechanic Helper": "CRAFT",
    "Mechanic Manager": "CRAFT",
    "Mechanical Drafter": "ENGINEER",
    "Mechanical Drafting Lead": "ENGINEER",
    "Mechanical Engineer": "ENGINEER",
    "Office Manager": "ADMIN",
    "Office Manager (Hourly)": "ADMIN",
    "Operator": "CRAFT",
    "OPERATOR I (Steward)": "CRAFT",
    "Operator Helper": "CRAFT",
    "Operations Manager": "MANAGEMENT",
    "Operations Manager, EPC": "MANAGEMENT",
    "Operations Supervisor": "CRAFT",
    "Painter": "CRAFT",
    "Payroll Manager": "ADMIN",
    "Pipe Fitter": "CRAFT",
    "Plant Manager": "MANAGEMENT",
    "Process Engineer": "ENGINEER",
    "Procurement Manager": "PROCUREMENT",
    "Procurement Specialist": "PROCUREMENT",
    "Production Manager": "MANAGEMENT",
    "Program Manager": "PM",
    "Project Admin Support": "ADMIN",
    "Project Controls Manager": "PM",
    "Project Controls Specialist": "PM",
    "Project Coordination Manager": "PROJECT COORDINATOR",
    "Project Coordinator": "PROJECT COORDINATOR",
    "Project Coordinator (Hourly)": "PROJECT COORDINATOR",
    "Project Coordinator (Salary)": "PROJECT COORDINATOR",
    "Project Engineer": "ENGINEER",
    "Project Estimator": "ESTIMATION",
    "Project Estimator, I&E": "ESTIMATION",
    "Project Foreman": "FOREMAN",
    "Project Manager": "PM",
    "Pumper": "CRAFT",
    "QA Technician": "QUALITY",
    "QA/QC Assistant": "QUALITY",
    "QA/QC Manager": "QUALITY",
    "QA/QC Specialist": "QUALITY",
    "Quality Assurance Specialist": "QUALITY",
    "Quality Control Specialist": "QUALITY",
    "Quality Manager": "QUALITY",
    "Quality Specialist": "QUALITY",
    "Receiving & Document Controls": "ADMIN",
    "Regional Fleet Manager": "MANAGEMENT",
    "Regional Manager, Northern": "MANAGEMENT",
    "Regional Operations Manager": "MANAGEMENT",
    "Regional Project Manager": "PM",
    "Rig Foreman": "FOREMAN",
    "Rig Manager": "MANAGEMENT",
    "Rigger": "CRAFT",
    "Rigger - A": "CRAFT",
    "Safety Specialist": "HSE",
    "Senior Civil Designer": "ENGINEER",
    "Senior Designer": "ENGINEER",
    "Senior Estimating Manager": "ESTIMATION",
    "Senior HSE Specialist": "HSE",
    "Senior Journeyman": "CRAFT",
    "Senior Procurement Specialist": "PROCUREMENT",
    "Senior Project Admin Support S": "ADMIN",
    "Senior Project Coordinator": "PROJECT COORDINATOR",
    "Senior Project Estimator": "ESTIMATION",
    "Senior Project Manager": "PM",
    "Senior QA/QC Manager": "QUALITY",
    "Shipping & Receiving Manager": "PROCUREMENT",
    "Shipping/Receiving Specialist": "PROCUREMENT",
    "Ship & Receive Superintendent": "PROCUREMENT",
    "Shop Foreman": "CRAFT",
    "Skilled Labor": "CRAFT",
    "Spacer": "CRAFT",
    "Splicer I": "CRAFT",
    "Splicer II": "CRAFT",
    "Splicing Manager": "FOREMAN",
    "Spotter": "CRAFT",
    "Sr Director, Delta Directional": "MANAGEMENT",
    "Sr Director, Specialty Service": "MANAGEMENT",
    "Sr. Project Admin Support": "ADMIN",
    "Strawboss": "CRAFT",
    "Structural Fab Superintendent": "SUPERINTENDENT",
    "Superintendent": "SUPERINTENDENT",
    "Superintendent (Hourly)": "SUPERINTENDENT",
    "Superintendent (Salary)": "SUPERINTENDENT",
    "Surveyor": "CRAFT",
    "Technical Sales Manager": "MANAGEMENT",
    "Testing & Cleaning Tech II": "CRAFT",
    "Tie-In Foreman": "FOREMAN",
    "Truck Driver": "CRAFT",
    "Validation/Verification Coach": "CRAFT",
    "V&V Program Manager": "MANAGEMENT",
    "VP, Specialty Services & CPG": "MANAGEMENT",
    "Vice President": "MANAGEMENT",
    "Vice President, Region 2": "MANAGEMENT",
    "Welder - (w/o Rig)": "CRAFT",
    "Welder - Certified (w/o rig)": "CRAFT",
    "Welder - In Service": "CRAFT",
    "Welder Foreman": "FOREMAN",
    "Welding Foreman": "FOREMAN",
    "Welding Superintendent": "SUPERINTENDENT",
    "Yard Foreman": "FOREMAN",
    "Yard Manager": "MANAGEMENT",
    "Welder": "CRAFT",
    "Forman - Welding (w/o Rig)": "CRAFT",
    "Craftsman A": "CRAFT",
    "Document Controls Manager": "MANAGEMENT",
    "Piping Division Lead": "MANAGEMENT",
    "Civil & Structural Divi. Lead": "MANAGEMENT",
    "Applications Manager (PKT)": "MANAGEMENT",
    "Assembly Foreman": "FOREMAN",
    "Assembly Technician": "CRAFT",
    "Assembly Technician (PKT)": "CRAFT",
    "Assistant Superintendent (H)": "SUPERINTENDENT",
    "Assistant, Project Manager": "MANAGEMENT",
    "Corrosion Supervisor": "FOREMAN",
    "Corrosion Technician I": "CRAFT",
    "Design Manager": "ENGINEER",
    "Designer II": "ENGINEER",
    "Designer IV": "ENGINEER",
    "Division Lead": "MANAGEMENT",
    "Drill Foreman": "FOREMAN",
    "Electrical Journeyman I": "CRAFT",
    "Electrical Journeyman II": "CRAFT",
    "Field Service Technician II": "CRAFT",
    "HSE Coordinator": "HSE",
    "HSE Specialist I": "HSE",
    "HSE Specialist II": "HSE",
    "HSE Specialist III": "HSE",
    "Helper I": "CRAFT",
    "Lead Electrical Engineer": "ENGINEER",
    "Lead HSE Specialist": "HSE",
    "Project Director": "MANAGEMENT",
    "QA/QC Lead": "QUALITY",
    "Regional Manager": "MANAGEMENT",
    "Sr. Equipment Operator-Crane": "CRAFT",
    "Sr. Program Manager": "MANAGEMENT",
    "Warehouse Manager": "MANAGEMENT",
    "Welding Foreman (PKT)": "FOREMAN"
}

const BurnToolPhased = () => {
    const trackApi = useApiTracker('burnToolLabor');
    // Wrap company.js APIs so every load/save shows up on the per-page indicator.
    const updateUserDataArray = (data) => trackApi('updateUserDataArray', 'save', () => _updateUserDataArray(data)).catch(() => {});
    const loadUserData = (params) => trackApi('loadUserData', 'load', () => _loadUserData(params));
    const loadHEETData = (params) => trackApi('loadHEETData', 'load', () => _loadHEETData(params));
    const [phases, setPhases] = useState([])
    const [phasesBudget, setPhasesBudget] = useState([])
    const [phaseDescriptions, setPhaseDescriptions] = useState([])
    const [phaseCodes, setPhaseCodes] = useAtom(jobPhaseCodesAtom);
    const [currentBudgetDollars, setCurrentBudgetDollars] = useState(0)
    const [currentBudgetHours, setCurrentBudgetHours] = useState(0)
    const [dayZeroDollars, setDayZeroDollars] = useState(0)
    const [dayZeroHourTotal, setDayZeroHourTotal] = useState(0)
    const [coDollars, setCoDollars] = useState(0)
    const [coHours, setCoHours] = useState(0)
    const [actualDollars, setActualDollars] = useState(0)
    const [actualHours, setActualHours] = useState(0)
    const [etcDollars, setEtcDollars] = useState(0)
    const [etcHours, setEtcHours] = useState(0)
    const [totalDollars, setTotalDollars] = useState(0)
    const [totalHours, setTotalHours] = useState(0)
    const [overUnderDollars, setOverUnderDollars] = useState(0)
    const [overUnderHours, setOverUnderHours] = useState(0)
    const [hasWkActuals, setHasWkActuals] = useState('No')
    const [selectedJob, setSelectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion, setSelectedVersion] = useAtom(selectedVersionAtom);
    const [selectedJobData, setSelectedJobData] = useAtom(selectedJobDataAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [jobCostDetail, setJobCostDetail] = useAtom(jobCostDetailAtom);
    const [jobData, setJobData] = useAtom(jobDataAtom);
    const [completionDateForecast, setCompletionDateForecast] = useAtom(projectCompleteAtom);
    const [startDate, setStartDate] = useAtom(startDateActualAtom)
    const [versions, setVersions] = useAtom(dataSourceVersionAtom)
    const [isLiveVersion, setIsLiveVersion] = useAtom(isLiveVersionAtom)
    const [lockedVersion, setLockedVersion] = useAtom(selectedVersionLockedAtom)
    const [userName] = useAtom(userNameAtom);
    const [userId] = useAtom(userIdAtom);
    const [userEmailCheck] = useAtom(userEmailCheckAtom);
    const guardAdmin = process.env.NODE_ENV !== 'production' || userId === '154045' || userId === '147219' || userEmailCheck === 'cameron.pitre@strikeusa.com' || userEmailCheck === 'aeron.griggs@strikeusa.com' || userEmailCheck === 'christopher.ellis@strikeusa.com';
    const canSeeWipTabs = true;
    const [userRole] = useAtom(userRoleAtom);
    const [previewRole] = useAtom(previewRoleAtom);
    // Project Coordinators only get the Manpower Planning subtab — the phased
    // labor cost grid never renders for them (see pcAllowedTabs in db/company.js).
    // Derived (not initial state) so it holds even when the role loads late.
    const isPC = effectiveIsProjectCoordinator(previewRole, userRole, userEmailCheck) || effectiveIsBuyer(previewRole, userRole, userEmailCheck);
    const [jobHEETData, setJobHEETData] = useAtom(jobHEETDataAtom);
    const [heetWeeklyData, setHeetWeeklyData] = useState({})
    const [heetPhaseWeeklyData, setHeetPhaseWeeklyData] = useState({})
    const [heetPhaseTotals, setHeetPhaseTotals] = useState({})
    const [heetTotal, setHeetTotal] = useState(0)

    const [expandedWeeks, setExpandedWeeks] = useState(false)
    const [weekRangeLeft, setWeekRangeLeft] = useState(0)
    const [weekRangeRight, setWeekRangeRight] = useState(0)
    const [projectWeeks, setProjectWeeks] = useState([])
    const [projectWeeksCloseToToday, setProjectWeeksCloseToToday] = useState([])
    const [projectWeeksData, setProjectWeeksData] = useState([{}])
    const [projectDates, setProjectDates] = useState([])
    const [textTimeout, setTextTimeout] = useState(null)
    const [dollarsExpanded, setDollarsExpanded] = useState(false)
    const [hoursExpanded, setHoursExpanded] = useState(false)
    const [dollarTotals, setDollarTotals] = useState([])
    const [hourTotals, setHourTotals] = useState([])
    const [totalsExpanded, setTotalsExpanded] = useState(false)
    const [dayZeroPhaseTotals, setDayZeroPhaseTotals] = useState({})
    const [dayZeroPhaseHourTotals, setDayZeroPhaseHourTotals] = useState({})
    const [coPhaseTotals, setCoPhaseTotals] = useState({})
    const [coPhaseHourTotals, setCoPhaseHourTotals] = useState({})
    const [actualPhaseTotals, setActualPhaseTotals] = useState({})
    const [actualPhaseHourTotals, setActualPhaseHourTotals] = useState({})
    const [estPhaseTotals, setEstPhaseTotals] = useState({})
    const [estPhaseHourTotals, setEstPhaseHourTotals] = useState({})
    const [actualPhaseWeekDollars, setActualPhaseWeekDollars] = useState({})
    const [versionInfo, setVersionInfo] = useState({})
    const containerRef = useRef(null);
    const phaseRefs = useRef({});
    // Survives WeekValue unmount/remount. Keyed by 'rowId=week', stores the
    // user's last typed values so a parent re-render mid-edit doesn't wipe the
    // input back to the (still-unsaved) value in dataRows. Cleared per-key when
    // dataRows reflects the typed value (see WeekValue's seed logic).
    const userWeekInputRef = useRef({});

    const [costPhaseInfo, setCostPhaseInfo] = useState({})
    const [isExportingTemplate, setIsExportingTemplate] = useState(false)
    // Upload of an edited export: { fileName, result } while the preview is
    // open, 'reading' while the file parses, false when closed.
    const [uploadPreview, setUploadPreview] = useState(false)
    const [uploadApplying, setUploadApplying] = useState(false)
    const uploadInputRef = useRef(null)
    const [selectedPhaseTab, setSelectedPhaseTab] = useState('all')
    const [collapsedPhases, setCollapsedPhases] = useState('init')
    const [showGraphView, setShowGraphView] = useState(false)
    const [topSectionExpanded, setTopSectionExpanded] = useState(false)
    const [manpowerOpen, setManpowerOpen] = useState(false)
    const [mpSubTab, setMpSubTab] = useState('burn') // 'burn' | 'manpower'
    const activeMpTab = isPC ? 'manpower' : mpSubTab;

    const [userData, setUserData] = useState(false)
    const [laborGraphsData, setLaborGraphsData] = useState({})
    const [phaseHoursData, setPhaseHoursData] = useState({})
    const [plannedHoursTotal, setPlannedHoursTotal] = useState(0)
    const [scorecardData, setScorecardData] = useState({})
    const [dayZeroPhaseQuantities, setDayZeroPhaseQuantities] = useState({})
    const [coPhaseQuantities, setCoPhaseQuantities] = useState({})
    const [heavyJobInstalledByPhase, setHeavyJobInstalledByPhase] = useState({})

    useEffect(() => {
        setTotalDollars(fNum(pNum(actualDollars) + pNum(etcDollars), 1))
        setTotalHours(fNum(pNum(actualHours) + pNum(etcHours), 1))
        setOverUnderDollars(fNum((pNum(actualDollars) + pNum(etcDollars)) - pNum(currentBudgetDollars), 1))
        setOverUnderHours(fNum((pNum(actualHours) + pNum(etcHours)) - pNum(currentBudgetHours), 1))
    }, [actualDollars, actualHours, etcDollars, etcHours, currentBudgetDollars, currentBudgetHours])

    useEffect(() => {
        if (selectedJob && selectedVersion) {
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion }).then((data) => {
                setJobUserData(data?.data);
            })
        }
    }, [selectedJob, selectedVersion])

    useEffect(() => {
        // Load HEET data when job changes
        if (selectedJob) {
            loadHEETData({ id: selectedJob }).then((data) => {
                setJobHEETData(data?.data);
            })
        }
    }, [selectedJob])

    const reloadUserData = () => {
        loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion }).then((data) => {
            // Fresh server data arriving — clear the typed-input shadow so the
            // newly loaded values are what WeekValue seeds from on next mount.
            userWeekInputRef.current = {}
            setJobUserData(data?.data);
        })
    }

    useEffect(() => {
        const container = containerRef.current;

        if (container) {
            const containerWidth = window.innerWidth;
            const scrollWidth = container.scrollWidth;


            if (scrollWidth > containerWidth) {
                const middlePosition = (scrollWidth - containerWidth) / 2;
                // the page scrolls inside the shell's scroll area, not the window
                (document.querySelector('[data-scroll-area]') || window).scrollTo({
                    left: middlePosition,
                    behavior: 'smooth'
                });
            }
        }
    }, [expandedWeeks, weekRangeLeft, weekRangeRight])

    useEffect(() => {
        versions && versions.length > 0 && setVersionInfo(versions.find(v => v.Version_GUID === selectedVersion))
    }, [versions]);

    useEffect(() => {
        const uData = {}
        const uDataNew = {}
        const dzData = {}
        const coData = {}
        const scData = {}
        const lgData = {}
        const phData = {}
        if (jobUserData && jobUserData.length > 0) {
            jobUserData.forEach((d) => {
                if (d?.Forecast_Tab === 'Scorecard') {
                    if (scData[d?.Forecast_Row_ID]) {
                        scData[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    } else {
                        scData[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                } else if (d?.Forecast_Tab === 'Labor Graphs') {
                    if (lgData[d?.Forecast_Row_ID]) {
                        lgData[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    } else {
                        lgData[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                } else if (d?.Forecast_Tab === 'Burn Tool Labor Phased') {
                    if (uData[d?.Forecast_Row_ID]) {
                        uData[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    } else {
                        uData[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }

                    uDataNew[d?.Forecast_Row_ID] = {
                        id: d?.Forecast_Row_ID,
                        ...d
                    }
                } else if (d?.Forecast_Tab === 'Day Zero Budget') {
                    dzData[d?.Forecast_Row_ID] = d
                } else if (d?.Forecast_Tab === 'Change Order') {
                    coData[d?.Forecast_User_Data_GUID || d?.Forecast_Row_ID] = { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label }
                } else if (d?.Forecast_Tab === 'Phase Hours' && d?.Version_GUID === selectedVersion && d?.Forecast_Field_Name?.startsWith('week_')) {
                    const week = d.Forecast_Field_Name.replace('week_', '')
                    const cc = d.Forecast_Row_ID
                    if (!phData[cc]) phData[cc] = {}
                    phData[cc][week] = (phData[cc][week] || 0) + (parseFloat(d.Forecast_Value) || 0)
                }
            })

            console.log('uData', uData, 'uData2', uDataNew)

            let totalCOCost = 0
            let totalCOHours = 0
            let dayZeroTotal = 0
            let dayZeroHourTotal = 0

            let cOPhaseTotals = {}
            let cOPhaseHourTotals = {}
            console.log('coData', coData)
            Object.values(coData).forEach((row) => {
                if (row['status'] === 'Approved' || row['Status'] === 'Approved') {
                    totalCOCost = totalCOCost + pNum(row['51100']) + pNum(row['51200']) + pNum(row['51300']) + pNum(row['51400']) + pNum(row['51500']) + pNum(row['51600']) + pNum(row['51700'])
                    
                    // Only count labor hours (not equipment hours). Field name goes stale when
                    // hours are added to a line saved dollars-first, so fall back to Cost_Type.
                    const isLaborHours = row['Forecast_Field_Name'] === 'totalLaborHours' ||
                        (String(row['Cost_Type'] || '').trim() === '51100' && row['Forecast_Field_Name'] !== 'totalEquipmentHours')
                    const laborHours = isLaborHours ? pNum(row['Hours']) : 0
                    totalCOHours = totalCOHours + laborHours

                    const phase = row['Cost_Code'] ? row['Cost_Code'] :''
                    if (cOPhaseTotals[phase]) {
                        cOPhaseTotals[phase] = cOPhaseTotals[phase] + pNum(row['51100']) + pNum(row['51200']) + pNum(row['51300']) + pNum(row['51400']) + pNum(row['51500']) + pNum(row['51600']) + pNum(row['51700'])
                    } else {
                        cOPhaseTotals[phase] = pNum(row['51100']) + pNum(row['51200']) + pNum(row['51300']) + pNum(row['51400']) + pNum(row['51500']) + pNum(row['51600']) + pNum(row['51700'])
                    }

                    if (cOPhaseHourTotals[phase]) {
                        cOPhaseHourTotals[phase] = cOPhaseHourTotals[phase] + laborHours
                    } else {
                        cOPhaseHourTotals[phase] = laborHours
                    }
                }
            })

            let dayZeroCostTotals = {}
            let dayZeroHourTotals = {}
            Object.values(dzData).forEach((row) => {
                const c = row['Cost_Type']
                if (row['Value_Amount'] && (c == '51100' || c == '51200' || c == '51300' || c == '51400' || c == '51500' || c == '51600' || c == '51700')) {
                    dayZeroTotal = dayZeroTotal + (pNum(row['Value_Amount']) ? pNum(row['Value_Amount']) : 0)
                    dayZeroHourTotal = dayZeroHourTotal + (pNum(row['Hours']) ? pNum(row['Hours']) : 0)
                }

                if (dayZeroCostTotals[row['Cost_Code']]) {
                    dayZeroCostTotals[row['Cost_Code']] = dayZeroCostTotals[row['Cost_Code']] + (pNum(row['Value_Amount']) ? pNum(row['Value_Amount']) : 0)
                } else {
                    dayZeroCostTotals[row['Cost_Code']] = (pNum(row['Value_Amount']) ? pNum(row['Value_Amount']) : 0)
                }

                if (dayZeroHourTotals[row['Cost_Code']]) {
                    dayZeroHourTotals[row['Cost_Code']] = dayZeroHourTotals[row['Cost_Code']] + (pNum(row['Hours']) ? pNum(row['Hours']) : 0)
                } else {
                    dayZeroHourTotals[row['Cost_Code']] = (pNum(row['Hours']) ? pNum(row['Hours']) : 0)
                }
            })

            let etcTotalCost = 0
            let etcTotalHours = 0
            Object.values(uDataNew).forEach((row) => {
                const week = row.id.split('=')[1]
                if (row.Number_Hours && row.Rate && row.Number_Units && moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(completionDateForecast).endOf('week'))) {
                    const h = row.Number_Hours ? row.Number_Hours : 0
                    etcTotalCost = etcTotalCost + (!isNaN(pNum(h)) && !isNaN(pNum(row.Rate)) && !isNaN(pNum(row.Number_Units)) ? pNum(h) * pNum(row.Number_Units) * pNum(row.Rate) : 0)
                    //console.log('etcTotalCost', row.Cost_Code,  etcTotalCost, pNum(h) * pNum(row.Number_Units)* pNum(row.Rate))
                    etcTotalHours = etcTotalHours + (!isNaN(pNum(h)) && !isNaN(pNum(row.Number_Units)) ? pNum(h) * pNum(row.Number_Units) : 0)
                }
            })


            setDayZeroPhaseTotals(dayZeroCostTotals)
            setDayZeroPhaseHourTotals(dayZeroHourTotals)
            setCoPhaseTotals(cOPhaseTotals)
            setCoPhaseHourTotals(cOPhaseHourTotals)

            setEtcDollars(fNum(etcTotalCost, 1))
            setEtcHours(fNum(etcTotalHours, 1))
            setDayZeroDollars(fNum(dayZeroTotal, 1))
            setDayZeroHourTotal(fNum(dayZeroHourTotal, 1))
            setCoDollars(fNum(totalCOCost, 1))
            setCoHours(fNum(totalCOHours, 1))
            setCurrentBudgetDollars(fNum(pNum(totalCOCost) + pNum(dayZeroTotal), 1))
            setCurrentBudgetHours(fNum(pNum(totalCOHours) + pNum(dayZeroHourTotal), 1))
            const costTypeDayZero = {}
            Object.values(dzData).forEach((row) => {
                if (costTypeDayZero[row['Cost_Code']]) {
                    costTypeDayZero[row['Cost_Code']].push(row)
                } else {
                    costTypeDayZero[row['Cost_Code']] = [row]
                }
            })

            // Track day zero quantities per phase
            const dzPhaseQty = {}
            Object.values(dzData).forEach((row) => {
                const qty = isNaN(pNum(row['Quantity'])) ? 0 : pNum(row['Quantity'])
                if (dzPhaseQty[row['Cost_Code']]) {
                    dzPhaseQty[row['Cost_Code']] += qty
                } else {
                    dzPhaseQty[row['Cost_Code']] = qty
                }
            })

            // Track CO quantities per phase
            const coPhaseQty = {}
            Object.values(coData).forEach((row) => {
                if (row['status'] === 'Approved' || row['Status'] === 'Approved') {
                    const phase = row['Cost_Code'] ? row['Cost_Code'] : ''
                    const qty = isNaN(pNum(row.Quantity)) ? 0 : pNum(row.Quantity)
                    if (coPhaseQty[phase]) {
                        coPhaseQty[phase] += qty
                    } else {
                        coPhaseQty[phase] = qty
                    }
                }
            })

            setDayZeroPhaseQuantities(dzPhaseQty)
            setCoPhaseQuantities(coPhaseQty)
            setScorecardData(scData)
            setPhasesBudget(costTypeDayZero)
            setUserData(uDataNew)
            setLaborGraphsData(lgData)
            setPhaseHoursData(phData)

            // Compute total planned hours from Labor Graphs
            let totalPlannedHrs = 0
            Object.values(lgData).forEach((row) => {
                totalPlannedHrs += pNum(row.plannedHours) || 0
            })
            setPlannedHoursTotal(fNum(totalPlannedHrs, 1))
        }
    }, [jobUserData])

    useEffect(() => {
        if (completionDateForecast) {
            let dateSections = {}
            let uniqueWeeks = {}
            let uniqueWeeksCloseToToday = {}
            // Anchor the week columns to the earliest of the actual start date
            // (set via the date-range picker / Set Dates) and the planned start,
            // so the grid begins where the range chip says and no early actuals/ETC get clipped.
            const startCandidates = [selectedJobData?.Planned_Start_Date, startDate].filter(Boolean).map(d => moment(d))
            const plannedStart = startCandidates.length ? moment.min(startCandidates) : moment('')
            const plannedFinish = moment(completionDateForecast)
            const fiveWeeksAgo = moment(versionInfo.Version_Start_DateTime).add(-7, 'days').subtract(5, 'weeks').startOf('week');
            const fiveWeeksLater = moment(versionInfo.Version_Start_DateTime).add(-7, 'days').add(10, 'weeks').startOf('week');

            let currentDay = plannedStart
            let currentWeek = plannedStart.startOf('isoWeek')
            const days = plannedFinish.diff(plannedStart, 'days')
            for (let i = 0; i < days; i++) {
                const day = currentDay.clone()
                const week = currentDay.clone()
                day.add(i, 'days')
                week.add(i, 'days')
                currentWeek = week.startOf('isoWeek')

                if (currentWeek.isBetween(fiveWeeksAgo, fiveWeeksLater, 'week', '[]')) {
                    uniqueWeeksCloseToToday[currentWeek.format('YYYY-MM-DD')] = 1
                }
                const currentDayFormatted = day.format('YYYY-MM-DD')
                uniqueWeeks[currentWeek.format('YYYY-MM-DD')] = 1
                dateSections[currentDayFormatted] = [currentWeek.format('YYYY-MM-DD')]
            }

            setProjectDates(dateSections)
            setProjectWeeks(Object.keys(uniqueWeeks))
            setProjectWeeksCloseToToday(Object.keys(uniqueWeeksCloseToToday))
        }
    }, [selectedJobData, versionInfo, completionDateForecast, startDate])

    useEffect(() => {
        const phases = {}
        let totalCost = 0
        let totalHours = 0
        console.log('cost detail', jobCostDetail?.[0])
        jobCostDetail?.forEach((row) => {
            if (row.Cost_Type_Code[0] == '5' && row.Cost_Type_Code[1] == '1' && row.Cost_Type_Code != '51800') {
                totalCost += pNum(row.Job_Cost)
                totalHours += pNum(row.Total_Hours)
                if (!phases[row.Phase_Code] && row.Phase_Code) {
                    phases[row.Phase_Code] = [row]
                } else if (row.Phase_Code) {
                    phases[row.Phase_Code].push(row)
                }
            }
        })

        const ph = Object.keys(phases)
        const phDescriptions = []
        //const phaseInfo = {}
        ph.forEach((p) => {
            if (p != '1000' && p != '899998') {
                //phaseInfo[p] = true
                phDescriptions[p] = {
                    description: phases[p][0].Phase_Code_Description,
                    code: phases[p][0].Phase_Code
                }
            }
        })
        // Calculate Heavy Job installed quantities from FP document type
        const hjInstalled = {}
        jobCostDetail?.forEach((row) => {
            if (row.Document_Type == 'FP' && row.Phase_Code) {
                if (!hjInstalled[row.Phase_Code]) {
                    hjInstalled[row.Phase_Code] = 0
                }
                hjInstalled[row.Phase_Code] += pNum(row.Total_Hours) || 0
            }
        })
        setHeavyJobInstalledByPhase(hjInstalled)

        setActualDollars(fNum(totalCost, 1))
        setActualHours(fNum(totalHours, 1))
        setPhases(phases)
        setCostPhaseInfo(phDescriptions)
    }, [jobCostDetail])

    useEffect(() => {
        const otherPhases = [...Object.keys(dayZeroPhaseTotals), ...Object.keys(coPhaseTotals)]
        //console.log('otherPhases', otherPhases, 'costPhaseInfo', costPhaseInfo)
        let otherPhaseDescriptions = []
        otherPhases.forEach((p) => {
            if (!costPhaseInfo[p]) {
                otherPhaseDescriptions[p] = {
                    description: phaseCodes[p] ? phaseCodes[p] : '',
                    code: p
                }
            }
        })
        // A phase with crew-plan dollars always gets a row, even once it has left the
        // budget: the Forecast tab sums every phased row, so a plan left on a retired
        // phase code would otherwise count there with nowhere to see or clear it.
        Object.values(userData || {}).forEach((row) => {
            const [p, week] = String(row?.id || '').split('=')
            if (!p || !week || costPhaseInfo[p] || otherPhaseDescriptions[p]) return
            if (pNum(row.Number_Hours) * pNum(row.Rate) * pNum(row.Number_Units) > 0) {
                otherPhaseDescriptions[p] = {
                    description: phaseCodes[p] ? phaseCodes[p] : '',
                    code: p
                }
            }
        })
        const phases = Object.values(costPhaseInfo).concat(Object.values(otherPhaseDescriptions)).sort((a, b) => a.code - b.code)
        const filteredPhases = phases.filter(phase => phase.code !== '1000' && phase.code !== '990000');
        setPhaseDescriptions(filteredPhases)
    }, [costPhaseInfo, dayZeroPhaseTotals, coPhaseTotals, userData])

    useEffect(() => {
        if (phaseDescriptions?.length && jobUserData) {
            calculateTotals()
        }
    }, [userData, projectWeeks, phaseDescriptions, jobHEETData, laborGraphsData])

    const calculateTotals = () => {
        console.log('calculate totals source')
        let etcHours = 0
        let etcDollars = 0
        let projectData = {}

        let phaseActualHours = {}
        let phaseActualDollars = {}
        let phaseActualCrewCount = {}
        let phaseActualCrew = {}
        let phaseTotalHours = {}
        let phaseTotalDollars = {}
        let totalActualHours = 0
        let totalActualDollars = 0

        //console.log('cost detail', jobCostDetail)
        jobCostDetail && jobCostDetail.forEach((row) => {
            const phase = row.Phase_Code
            const date = moment(row.Consolidated_Date).add(12, 'hours').format('YYYY-MM-DD')
            const week = projectDates[date]

            if (row.Cost_Type_Code[0] == '5' && row.Cost_Type_Code[1] == '1' && row.Cost_Type_Code != '51800') {
                totalActualHours += pNum(row.Total_Hours)
                totalActualDollars += pNum(row.Job_Cost)

                if (phaseActualDollars[phase]?.[week]) {
                    phaseActualDollars[phase][week] = phaseActualDollars[phase][week] + pNum(row.Job_Cost)
                } else if (phaseActualDollars[phase]) {
                    phaseActualDollars[phase][week] = pNum(row.Job_Cost)
                } else {
                    phaseActualDollars[phase] = { [week]: pNum(row.Job_Cost) }
                }

                if (phaseActualHours[phase]?.[week]) {
                    phaseActualHours[phase][week] = phaseActualHours[phase][week] + pNum(row.Total_Hours)
                } else if (phaseActualHours[phase]) {
                    phaseActualHours[phase][week] = pNum(row.Total_Hours)
                } else {
                    phaseActualHours[phase] = { [week]: pNum(row.Total_Hours) }
                }

                if (row.Staff_ID && phaseActualCrew[week] && !phaseActualCrew[week][row.Staff_ID]) {
                    phaseActualCrew[week][row.Staff_ID] = 1
                    phaseActualCrewCount[week] = phaseActualCrewCount[week] + 1
                } else if (row.Staff_ID && !phaseActualCrew[week]) {
                    phaseActualCrew[week] = { [row.Staff_ID]: 1 }
                    phaseActualCrewCount[week] = 1
                }

                if (phaseTotalHours[phase]) {
                    phaseTotalHours[phase] = phaseTotalHours[phase] + pNum(row.Total_Hours)
                } else {
                    phaseTotalHours[phase] = pNum(row.Total_Hours)
                }

                if (phaseTotalDollars[phase]) {
                    phaseTotalDollars[phase] = phaseTotalDollars[phase] + pNum(row.Job_Cost)
                } else {
                    phaseTotalDollars[phase] = pNum(row.Job_Cost)
                }
            }
        })

        // Process HEET data by week and by phase+week
        const heetWeeks = {}
        const heetByPhase = {} // { phaseCode: { week: amount } }
        const heetPhaseTotalsCalc = {} // { phaseCode: totalAmount }
        let heetGrandTotal = 0
        if (jobHEETData && Array.isArray(jobHEETData)) {
            jobHEETData.forEach((row) => {
                if (row.GL_Date && row.GL_Amount) {
                    const rowDate = moment(row.GL_Date).startOf('isoWeek').format('YYYY-MM-DD')
                    const week = projectDates[rowDate] || rowDate
                    // Clean and trim phase code, handle null/undefined
                    let phase = row.Phase_Code ? String(row.Phase_Code).trim() : 'Unknown'
                    // Handle comma-separated duplicates (e.g., "100,100" -> "100")
                    if (phase.includes(',')) {
                        const parts = phase.split(',').map(p => p.trim())
                        phase = parts[0] || 'Unknown'
                        //console.warn('HEET Phase_Code contains comma, using first part:', row.Phase_Code, '->', phase)
                    }
                    const amt = pNum(row.GL_Amount)
                    // Overall weekly totals
                    heetWeeks[week] = (heetWeeks[week] || 0) + amt
                    // Phase+week breakdown
                    if (!heetByPhase[phase]) heetByPhase[phase] = {}
                    heetByPhase[phase][week] = (heetByPhase[phase][week] || 0) + amt
                    // Phase totals
                    heetPhaseTotalsCalc[phase] = (heetPhaseTotalsCalc[phase] || 0) + amt
                    heetGrandTotal += amt
                }
            })
        }
        setHeetWeeklyData(heetWeeks)
        setHeetPhaseWeeklyData(heetByPhase)
        setHeetPhaseTotals(heetPhaseTotalsCalc)
        setHeetTotal(heetGrandTotal)

        console.log('HEET - Weekly totals:', heetWeeks)
        console.log('HEET - By phase (keys):', Object.keys(heetByPhase))
        console.log('HEET - By phase (full):', JSON.stringify(heetByPhase, null, 2))
        console.log('HEET - Phase totals:', heetPhaseTotalsCalc)

        let totalEstHours = {}
        let totalEstDollars = {}
        let totalEstBurnRate = {}
        let totalEstHoursWeek = {}
        let totalEstCrewSize = {}
        projectWeeks && projectWeeks.forEach((week) => {
            let etcHoursWeek = 0
            let etcDollarsWeek = 0
            let actualHoursWeek = 0
            let actualDollarsWeek = 0
            let estBurnRate = 0
            let actualBurnRate = 0
            let estCrewSize = 0
            let actualCrewSize = 0
            let avgHoursWeekCount = 0

            phaseDescriptions.forEach((phase) => {
                const rowId = phase.code
                const w = userData[phase.code + '=' + week]
                if (w && moment(week).isBefore(moment(completionDateForecast))) {
                    // no burn rate entered = the entry contributes zero est hours,
                    // matching the week cells which display blank without a rate
                    const h = pNum(w.Rate) > 0 && w.Number_Hours ? w.Number_Hours : 0

                    etcHoursWeek = etcHoursWeek + (!isNaN(h) && !isNaN(w.Number_Units) ? pNum(h) * pNum(w.Number_Units) : 0)
                    etcDollarsWeek = etcDollarsWeek + (!isNaN(h) && !isNaN(w.Rate) && !isNaN(w.Number_Units) ? pNum(h) * pNum(w.Rate) * pNum(w.Number_Units) : 0)
                    estCrewSize = estCrewSize + (!isNaN(w.Number_Units) ? pNum(w.Number_Units) : 0)

                    if (moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week'))) {
                        etcDollars = etcDollars + (!isNaN(h) && !isNaN(w.Rate) && !isNaN(w.Number_Units) ? pNum(h) * pNum(w.Rate) * pNum(w.Number_Units) : 0)
                        etcHours = etcHours + (!isNaN(h) && !isNaN(w.Number_Units) ? pNum(h) * pNum(w.Number_Units) : 0)
                        if (totalEstHours[phase.code]) {
                            totalEstHours[phase.code] += (!isNaN(h) && !isNaN(w.Number_Units) ? pNum(h) * pNum(w.Number_Units) : 0)
                        } else {
                            totalEstHours[phase.code] = (!isNaN(h) && !isNaN(w.Number_Units) ? pNum(h) * pNum(w.Number_Units) : 0)
                        }

                        if (totalEstDollars[phase.code]) {
                            totalEstDollars[phase.code] += (!isNaN(h) && !isNaN(w.Rate) && !isNaN(w.Number_Units) ? pNum(h) * pNum(w.Rate) * pNum(w.Number_Units) : 0)
                        } else {
                            totalEstDollars[phase.code] = (!isNaN(h) && !isNaN(w.Rate) && !isNaN(w.Number_Units) ? pNum(h) * pNum(w.Rate) * pNum(w.Number_Units) : 0)
                        }
                    }
                }

                actualHoursWeek = actualHoursWeek + (phaseActualHours[phase.code]?.[week] ? phaseActualHours[phase.code][week] : 0)
                avgHoursWeekCount = avgHoursWeekCount + (phaseActualHours[phase.code]?.[week] ? 1 : 0)
                actualDollarsWeek = actualDollarsWeek + (phaseActualDollars[phase.code]?.[week] ? phaseActualDollars[phase.code][week] : 0)
            })

            //week totals
            projectData[week] = {
                actualDollars: actualDollarsWeek ? fNum(actualDollarsWeek, 1) : '',
                actualHours: actualHoursWeek ? fNum(actualHoursWeek, 1) : '',
                actualCrewSize: phaseActualCrewCount[week] ? fNum(phaseActualCrewCount[week]) : '',
                etcCrewSize: fNum(estCrewSize),
                etcDollars: fNum(etcDollarsWeek, 1),
                etcHours: fNum(etcHoursWeek, 1),
                estBurnRate: fNum(etcDollarsWeek && etcHoursWeek ? etcDollarsWeek / etcHoursWeek : 0),
                avgHoursWeek: phaseActualCrewCount[week] ? fNum((actualHoursWeek / phaseActualCrewCount[week]), 1) : '',
                actualBurnRate: fNum(actualDollarsWeek && actualHoursWeek ? actualDollarsWeek / actualHoursWeek : 0),
                plannedHours: laborGraphsData[week]?.plannedHours ? fNum(pNum(laborGraphsData[week].plannedHours), 1) : '',

            }
        })

        setActualPhaseTotals(phaseTotalDollars)
        setActualPhaseHourTotals(phaseTotalHours)
        setActualPhaseWeekDollars(phaseActualDollars)
        setEstPhaseTotals(totalEstDollars)
        setEstPhaseHourTotals(totalEstHours)

        setProjectWeeksData(projectData)
        setEtcDollars(fNum(etcDollars, 1))
        setEtcHours(fNum(etcHours, 1))
    }

    // Who exported what: one EXPORT row in the change log per download, so
    // the Audit Log page shows who still uses which export (Seth 11/3).
    const logExport = (which, filename) => {
        recordAudit({
            actionType: 'EXPORT',
            userEmail: userEmailCheck,
            userName,
            userId,
            buId: selectedJob || null,
            version: selectedVersion ? selectedVersion + '' : null,
            tab: 'Burn Tool Labor Phased',
            fieldName: which,
            newValue: filename,
        }).catch(() => {});
    };

    const exportToExcel = async () => {
        try {
            console.log('Starting Excel export...');
            
            // Create new workbook and worksheet
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Burn Tool Phased Data');

            // Get sorted week dates for columns
            const sortedWeeks = projectWeeks ? [...projectWeeks].sort() : [];

            // Define base headers
            const baseHeaders = [
                'Phase Code',
                'Phase Description',
                'Data Type', // Estimated Cost, Burn Rate, Hours/Week, etc.
                'Phase Budget Dollars',
                'Phase Budget Hours', 
                'Phase Actual Total Dollars',
                'Phase Actual Total Hours',
                'Phase ETC Dollars',
                'Phase ETC Hours'
            ];

            // Add week dates as columns
            const headers = [...baseHeaders, ...sortedWeeks];

            // Add header row
            const headerRow = worksheet.addRow(headers);
            headerRow.font = { bold: true };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE0E0E0' }
            };

            // Data types we want to export for each phase
            const dataTypes = [
                { key: 'estimatedCost', label: 'Estimated Cost' },
                { key: 'burnRate', label: 'Burn Rate' },
                { key: 'hoursWeek', label: 'Hours/Week' },
                { key: 'crewSize', label: 'Crew Size' },
                { key: 'actualCost', label: 'Actual Cost' },
                { key: 'actualBurnRate', label: 'Actual Burn Rate' },
                { key: 'actualHours', label: 'Actual Hours' },
                { key: 'estimatedHours', label: 'Estimated Hours' },
                { key: 'betweenInterval', label: 'Between Date Interval' }
            ];

            // Process each phase
            phaseDescriptions?.forEach((phase) => {
                if (!phase || !phase.code) return;

                const phaseCode = phase.code;
                const phaseDescription = phase.description || '';

                // Get phase totals (calculated once per phase)
                const phaseBudgetDollars = (phasesBudget[phaseCode] || [])
                    .filter(row => row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8')
                    .reduce((total, row) => total + (isNaN(pNum(row['Value_Amount'])) ? 0 : pNum(row['Value_Amount'])), 0) +
                    (isNaN(pNum(coPhaseTotals[phaseCode])) ? 0 : pNum(coPhaseTotals[phaseCode]));

                const phaseBudgetHours = (phasesBudget[phaseCode] || [])
                    .filter(row => row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8')
                    .reduce((total, row) => total + (isNaN(pNum(row.Hours)) ? 0 : pNum(row.Hours)), 0) +
                    (isNaN(pNum(coPhaseHourTotals[phaseCode])) ? 0 : pNum(coPhaseHourTotals[phaseCode]));

                const phaseActualTotalDollars = actualPhaseTotals[phaseCode] || 0;
                const phaseActualTotalHours = actualPhaseHourTotals[phaseCode] || 0;
                const phaseETCDollars = estPhaseTotals[phaseCode] || 0;
                const phaseETCHours = estPhaseHourTotals[phaseCode] || 0;

                // Get phase user data for start/end dates
                const phaseUserData = userData[phaseCode];
                const phaseStartDate = phaseUserData?.startDate;
                const phaseEndDate = phaseUserData?.endDate;

                // Pre-calculate actual data by week for this phase
                const actualDataByWeek = {};
                const phaseActualData = phases[phaseCode] || [];
                
                phaseActualData.forEach((row) => {
                    const date = moment(row.Consolidated_Date).add(12, 'hours').format('YYYY-MM-DD');
                    const rowWeek = projectDates[date];
                    if (rowWeek && row.Cost_Type_Code[0] == '5' && row.Cost_Type_Code[1] == '1' && row.Cost_Type_Code != '51800') {
                        if (!actualDataByWeek[rowWeek]) {
                            actualDataByWeek[rowWeek] = { cost: 0, hours: 0, staffIds: new Set() };
                        }
                        actualDataByWeek[rowWeek].cost += (isNaN(pNum(row.Job_Cost)) ? 0 : pNum(row.Job_Cost));
                        actualDataByWeek[rowWeek].hours += (isNaN(pNum(row.Total_Hours)) ? 0 : pNum(row.Total_Hours));
                        actualDataByWeek[rowWeek].staffIds.add(row.Staff_ID);
                    }
                });

                // Create rows for each data type
                dataTypes.forEach((dataType) => {
                    const rowData = [
                        phaseCode,
                        phaseDescription,
                        dataType.label,
                        fNum(phaseBudgetDollars, 1) || '',
                        fNum(phaseBudgetHours, 1) || '',
                        fNum(phaseActualTotalDollars, 1) || '',
                        fNum(phaseActualTotalHours, 1) || '',
                        fNum(phaseETCDollars, 1) || '',
                        fNum(phaseETCHours, 1) || ''
                    ];

                    // Add data for each week
                    sortedWeeks.forEach((week) => {
                        const weekData = userData[phaseCode + '=' + week];
                        const actualWeekData = actualDataByWeek[week];
                        
                        // Check if week is between phase start and end dates
                        const isBetweenInterval = phaseStartDate && phaseEndDate ? 
                            moment(week).isBetween(
                                moment(phaseStartDate).startOf('isoWeek').add(-1, 'day'), 
                                moment(phaseEndDate).add(1, 'day')
                            ) : false;

                        let cellValue = '';

                        switch (dataType.key) {
                            case 'estimatedCost':
                                if (weekData) {
                                    const cost = pNum(weekData.Rate || 0) * pNum(weekData.Number_Hours || 0) * pNum(weekData.Number_Units || 0);
                                    cellValue = cost > 0 ? fNum(cost, 1) : '';
                                }
                                break;
                            case 'burnRate':
                                cellValue = weekData?.Rate ? fNum(pNum(weekData.Rate)) : '';
                                break;
                            case 'hoursWeek':
                                cellValue = weekData?.Number_Hours ? fNum(pNum(weekData.Number_Hours), 1) : '';
                                break;
                            case 'crewSize':
                                cellValue = weekData?.Number_Units ? fNum(pNum(weekData.Number_Units)) : '';
                                break;
                            case 'actualCost':
                                cellValue = actualWeekData?.cost ? fNum(actualWeekData.cost, 1) : '';
                                break;
                            case 'actualBurnRate':
                                if (actualWeekData?.cost && actualWeekData?.hours) {
                                    cellValue = fNum(actualWeekData.cost / actualWeekData.hours, 1);
                                }
                                break;
                            case 'actualHours':
                                cellValue = actualWeekData?.hours ? fNum(actualWeekData.hours, 1) : '';
                                break;
                            case 'estimatedHours':
                                if (weekData) {
                                    const hours = pNum(weekData.Rate) > 0 ? pNum(weekData.Number_Hours || 0) * pNum(weekData.Number_Units || 0) : 0;
                                    cellValue = hours > 0 ? fNum(hours, 1) : '';
                                }
                                break;
                            case 'betweenInterval':
                                cellValue = isBetweenInterval ? 'Yes' : 'No';
                                break;
                        }

                        rowData.push(cellValue);
                    });

                    // Add the row to worksheet
                    worksheet.addRow(rowData);
                });
            });

            // Auto-fit columns
            worksheet.columns.forEach((column, index) => {
                let maxLength = headers[index]?.toString().length || 10;
                // Check a few rows to determine width
                for (let rowNum = 2; rowNum <= Math.min(worksheet.rowCount, 20); rowNum++) {
                    const cell = worksheet.getCell(rowNum, index + 1);
                    if (cell.value) {
                        maxLength = Math.max(maxLength, cell.value.toString().length);
                    }
                }
                column.width = Math.min(maxLength + 2, 20);
            });

            // Add borders to all cells
            worksheet.eachRow((row, rowNumber) => {
                row.eachCell((cell, colNumber) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
            });

            // Freeze header row and first 3 columns (Phase Code, Description, Data Type)
            worksheet.views = [{
                state: 'frozen',
                xSplit: 3,
                ySplit: 1,
                topLeftCell: 'D2'
            }];

            // Generate Excel file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });

            // Create filename with job and version info
            const filename = `BurnToolPhased_Job${selectedJob}_Ver${selectedVersion}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
            
            // Save file
            saveAs(blob, filename);
            
            console.log(`Excel export completed: ${filename}`);
            logExport('Export to Excel', filename);
            
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            alert('Error exporting data to Excel. Please try again.');
        }
    };

    // Reads an edited Export to Excel file and opens the review dialog. The
    // sheet is compared to userData as it stands; nothing is saved here.
    const handleUploadFile = async (event) => {
        const file = event.target.files && event.target.files[0];
        event.target.value = '';
        if (!file) return;
        setUploadPreview('reading');
        try {
            const parsed = await parseBurnToolPhasedWorkbook(await file.arrayBuffer());
            const result = diffBurnToolPhased(parsed, {
                userData,
                projectWeeks,
                phaseCodes: (phaseDescriptions || []).map(p => p?.code).filter(Boolean),
                versionStart: versionInfo?.Version_Start_DateTime,
                completion: completionDateForecast,
            });
            setUploadPreview({ fileName: file.name, result });
        } catch (error) {
            console.error('Error reading burn tool upload:', error);
            setUploadPreview(false);
            alert(error?.message || 'Could not read that file.');
        }
    };

    // Apply: one request carrying every changed week, then reload so the grid
    // shows what the server now holds.
    const applyUpload = async () => {
        if (!uploadPreview || uploadPreview === 'reading' || lockedVersion) return;
        setUploadApplying(true);
        try {
            const rows = buildBurnToolPhasedRows(uploadPreview.result.finals, {
                selectedJob, selectedVersion, lockedVersion, selectedJobData, completion: completionDateForecast,
            });
            if (rows.length) await updateUserDataArray(rows);
            reloadUserData();
            setUploadPreview(false);
        } finally {
            setUploadApplying(false);
        }
    };

    const exportHEETToExcel = async () => {
        try {
            if (!jobHEETData || !Array.isArray(jobHEETData) || jobHEETData.length === 0) {
                alert('No HEET data available to export.');
                return;
            }
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('HEET Data');

            worksheet.columns = [
                { header: 'GL Date', key: 'GL_Date', width: 15 },
                { header: 'Phase Code', key: 'Phase_Code', width: 15 },
                { header: 'Cost Type Code', key: 'Cost_Type_Code', width: 18 },
                { header: 'GL Amount', key: 'GL_Amount', width: 15 },
                { header: 'Record Type', key: 'Record_Type', width: 15 },
                { header: 'Craft Description', key: 'Job_Type_Craft_Code_Description', width: 30 },
                { header: 'Units', key: 'Units', width: 12 },
                { header: 'BU ID', key: 'BU_ID', width: 12 },
            ];

            worksheet.getRow(1).font = { bold: true };
            worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };

            jobHEETData.forEach((row) => {
                worksheet.addRow({
                    GL_Date: row.GL_Date ? moment(row.GL_Date).format('MM/DD/YYYY') : '',
                    Phase_Code: row.Phase_Code || '',
                    Cost_Type_Code: row.Cost_Type_Code || '',
                    GL_Amount: row.GL_Amount != null ? pNum(row.GL_Amount) : '',
                    Record_Type: row.Record_Type || '',
                    Job_Type_Craft_Code_Description: row.Job_Type_Craft_Code_Description || '',
                    Units: row.Units != null ? pNum(row.Units) : '',
                    BU_ID: row.BU_ID || '',
                });
            });

            worksheet.getColumn('GL_Amount').numFmt = '$#,##0.00';

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const filename = `HEET_Data_Phased_Job${selectedJob}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
            saveAs(blob, filename);
        } catch (error) {
            console.error('Error exporting HEET data:', error);
            alert('Error exporting HEET data to Excel. Please try again.');
        }
    };

    // Export Template: Aeron's layout (see burnToolPhasedExport.js), with
    // the Manpower Curve sheet. The upload reads this file back as well as
    // the plain Export to Excel. The numbers are the tool's own: labor cost
    // types 51x less 518, the CO totals in the budget, the version's ETC week.
    const exportToExcelTemplate = async () => {
        setIsExportingTemplate(true);
        try {
            const sortedWeeks = projectWeeks ? [...projectWeeks].sort() : [];
            const isLabor = (ct) => ct && ct[0] == '5' && ct[1] == '1' && ct[2] != '8';
            const phasesOut = [];

            phaseDescriptions?.forEach((phase) => {
                if (!phase || !phase.code) return;
                const phaseCode = phase.code;
                const budgetRows = (phasesBudget[phaseCode] || []).filter(row => isLabor(row['Cost_Type']));
                const budgetDollars = budgetRows.reduce((t, row) => t + (isNaN(pNum(row['Value_Amount'])) ? 0 : pNum(row['Value_Amount'])), 0) +
                    (isNaN(pNum(coPhaseTotals[phaseCode])) ? 0 : pNum(coPhaseTotals[phaseCode]));
                const budgetHours = budgetRows.reduce((t, row) => t + (isNaN(pNum(row.Hours)) ? 0 : pNum(row.Hours)), 0) +
                    (isNaN(pNum(coPhaseHourTotals[phaseCode])) ? 0 : pNum(coPhaseHourTotals[phaseCode]));

                const phaseUserData = userData[phaseCode];
                const phaseStartDate = phaseUserData?.startDate;
                const phaseEndDate = phaseUserData?.endDate;

                const actualByWeek = {};
                (phases[phaseCode] || []).forEach((row) => {
                    const date = moment(row.Consolidated_Date).add(12, 'hours').format('YYYY-MM-DD');
                    const rowWeek = projectDates[date];
                    if (rowWeek && isLabor(row.Cost_Type_Code)) {
                        if (!actualByWeek[rowWeek]) actualByWeek[rowWeek] = { cost: 0, hours: 0 };
                        actualByWeek[rowWeek].cost += (isNaN(pNum(row.Job_Cost)) ? 0 : pNum(row.Job_Cost));
                        actualByWeek[rowWeek].hours += (isNaN(pNum(row.Total_Hours)) ? 0 : pNum(row.Total_Hours));
                    }
                });

                const weeksOut = {};
                sortedWeeks.forEach((week) => {
                    const weekData = userData[phaseCode + '=' + week];
                    const actual = actualByWeek[week];
                    const between = phaseStartDate && phaseEndDate ?
                        moment(week).isBetween(
                            moment(phaseStartDate).startOf('isoWeek').add(-1, 'day'),
                            moment(phaseEndDate).add(1, 'day')
                        ) : false;
                    weeksOut[week] = {
                        rate: pNum(weekData?.Rate || 0),
                        hours: pNum(weekData?.Number_Hours || 0),
                        crew: pNum(weekData?.Number_Units || 0),
                        actualCost: actual?.cost || 0,
                        actualHours: actual?.hours || 0,
                        between,
                    };
                });

                // The Manpower Curve sheet rolls phases up by discipline: the
                // bid's WBS section on a VoltaGrid job, the standard WBS otherwise.
                const discipline = (isVoltaGridJob(selectedJob) ? voltaGridWbsFor(phaseCode)?.name : null)
                    || standardDisciplineFor(phaseCode)?.discipline || '';
                phasesOut.push({ code: phaseCode, description: phase.description || '', discipline, budgetDollars, budgetHours, weeks: weeksOut });
            });

            const workbook = buildBurnToolPhasedWorkbook({
                phases: phasesOut,
                weeks: sortedWeeks,
                etcWeek: etcFromWeek(sortedWeeks, versionInfo?.Version_Start_DateTime),
                hoursPerWeek: planHoursPerWeek(phasesOut),
            });
            const buffer = await writeBurnToolPhasedXlsx(workbook);
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const filename = `BurnToolPhased_Template_Job${selectedJob}_Ver${selectedVersion}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
            saveAs(blob, filename);
            logExport('Export Template', filename);
        } catch (error) {
            console.error('Error exporting to Excel template:', error);
            alert('Error exporting data to Excel template. Please try again.');
        } finally {
            setIsExportingTemplate(false);
        }
    };


    const PhaseRow = (props) => {
        const phaseHeetData = props.heetData || {}
        const [data, setData] = useState(userData[props.phase.code])
        const [dataRows, setDataRows] = useState([])
        const [expandEst, setExpandEst] = useState(data?.expandEst ? data?.expandEst : true)
        const [expandAct, setExpandAct] = useState(data?.expandAct ? data?.expandAct : false)
        const [expandSupplierHours, setExpandSupplierHours] = useState(false)
        const [code, setCode] = useState()
        const [codeDescription, setCodeDescription] = useState()
        const [phaseActualDollars, setPhaseActualDollars] = useState({})
        const [phaseActualHours, setPhaseActualHours] = useState({})
        const [phaseActualCrewCount, setPhaseActualCrewCount] = useState({})
        const [supplierHoursByWeek, setSupplierHoursByWeek] = useState({}) // { week: { supplierName: hours } }
        const [supplierNames, setSupplierNames] = useState([]) // sorted unique supplier names
        const [classificationHoursByWeek, setClassificationHoursByWeek] = useState({}) // { week: { classification: hours } }
        const [classificationNames, setClassificationNames] = useState([]) // sorted unique classification groups
        const [expandClassificationHours, setExpandClassificationHours] = useState(false)
        const [expandedClassifications, setExpandedClassifications] = useState({}) // { classificationName: boolean }
        const [classificationPeopleByWeek, setClassificationPeopleByWeek] = useState({}) // { classification: { week: { personName: hours } } }
        const [classificationPeopleNames, setClassificationPeopleNames] = useState({}) // { classification: [sorted names] }
        const [phaseTotalHours, setPhaseTotalHours] = useState(0)
        const [phaseTotalDollars, setPhaseTotalDollars] = useState(0)
        const [phaseBudgetHours, setPhaseBudgetHours] = useState(0)
        const [phaseBudgetDollars, setPhaseBudgetDollars] = useState(0)
        const [expanded, setExpanded] = useState(data?.expanded ? data?.expanded : true)
        const [startDate, setStartDate] = useState(data?.Phase_Start ? moment(data?.Phase_Start).add(6, 'hours').format('YYYY-MM-DD') : '')
        const [endDate, setEndDate] = useState(data?.Phase_End ? moment(data?.Phase_End).add(6, 'hours').format('YYYY-MM-DD') : '')
        const [burnRateTopEst, setBurnRateEst] = useState(data?.Rate ? data?.Rate : '')
        const [hrsPerWeekEst, setHrsPerWeekEst] = useState(data?.Number_Hours ? data?.Number_Hours : 0)
        const [crewSizeEst, setCrewSizeEst] = useState(data?.Number_Units ? data?.Number_Units : '')
        const [estHours, setEstHours] = useState(0)
        const [estDollars, setEstDollars] = useState(0)
        const [estAtCompletionHours, setEstAtCompletionHours] = useState(0)
        const [estAtCompletionDollars, setEstAtCompletionDollars] = useState(0)
        const [overUnderDollars, setOverUnderDollars] = useState(0)
        const [overUnderHours, setOverUnderHours] = useState(0)
        const rowId = props.phase.code

        const [burnRateBudget, setBurnRateBudget] = useState(0)
        const [burnRateActual, setBurnRateActual] = useState(0)
        const [burnRateEtc, setBurnRateEtc] = useState(0)
        const [burnRateAtCompletion, setBurnRateAtCompletion] = useState(0)
        const [burnRateOverUnder, setBurnRateOverUnder] = useState(0)


        useEffect(() => {
            const userLocalData = {}
            userData && Object.keys(userData).forEach((key) => {
                if (key.includes(rowId)) {
                    userLocalData[key] = userData[key]
                }
            })
            //console.log('userLocalData', userLocalData)
            setDataRows(userLocalData)
        }, [userData])

        const debounce = (func, delay) => {
            let timeoutId;
            return (...args) => {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                timeoutId = setTimeout(() => {
                    func(...args);
                }, delay);
            };
        }

        const saveValue = (burnRate, hoursWeek, crewSize, start, end) => {
            let data = {
                userId: userId || 'seth.bailey@strikeusa.com',
                userName: userName || 'Seth Bailey',
                fieldName: 'row',
                tab: "Burn Tool Labor Phased",
                rowId: rowId,
                version: selectedVersion + '',
                value: burnRate && hoursWeek && crewSize ? pNum(burnRate) * pNum(hoursWeek) * pNum(crewSize) : '',
                valueAmount: burnRate && hoursWeek && crewSize ? pNum(burnRate) * pNum(hoursWeek) * pNum(crewSize) : '',
                id: selectedJob,
                costCode: rowId,
                costType: '51100',
                lockedVersion: lockedVersion,
                projectStart: startDate,
                projectEnd: completionDateForecast,
                // phaseStart: start ? moment(start).add(6, 'hours').toISOString() : startDate ? moment(startDate).add(6, 'hours').toISOString() : '',
                // phaseEnd: end ? moment(end).add(6, 'hours').toISOString() : endDate ? moment(endDate).add(6, 'hours').toISOString() : '',
                phaseStart: start ? start : startDate,
                phaseEnd: end ? end : endDate,
                rate: burnRate ? burnRate : burnRateTopEst,
                numberHours: hoursWeek ? hoursWeek : hrsPerWeekEst,
                numberUnits: crewSize ? crewSize : crewSizeEst
            }
            console.log('saveValue', burnRate, hoursWeek, crewSize, start, end, data)
            // Header + every touched week go up as ONE request. A request per week ran
            // them concurrently and SQL picked some as deadlock victims, silently
            // dropping those weeks.
            const rows = [data]

            const fieldsThatTriggerCalculation = { 'burnRateTopEst': 1, 'hrsPerWeekEst': 1, 'crewSizeEst': 1, 'startDate': 1, 'endDate': 1 }

            const sDate = start ? start : startDate
            const eDate = end ? end : endDate
            const cSizeEst = crewSize ? crewSize : crewSizeEst
            const bRateEst = burnRate ? burnRate : burnRateTopEst
            const hPerWeekEst = hoursWeek ? hoursWeek : hrsPerWeekEst

            let uniqueWeeks = {}

            const plannedStart = moment(sDate)
            const plannedFinish = moment(eDate)
            let currentDay = plannedStart
            let currentWeek = plannedStart
            const days = plannedFinish.diff(plannedStart, 'days')
            const lastWeekBeforeForecast = moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('week');
            for (let i = 0; i < days + 1; i++) {
                const day = currentDay.clone()
                const week = currentDay.clone()
                day.add(i, 'days')
                week.add(i, 'days')
                currentWeek = week.startOf('isoWeek')
                if (moment(currentWeek).isAfter(lastWeekBeforeForecast, 'week')) {
                    uniqueWeeks[currentWeek.format('YYYY-MM-DD')] = 1
                }
            }

            let uData = JSON.parse(JSON.stringify(dataRows))
            uData[rowId] = {
                burnRateTopEst: bRateEst,
                hrsPerWeekEst: hPerWeekEst,
                crewSizeEst: cSizeEst,
                startDate: sDate,
                endDate: eDate,
                expandEst: expandEst,
                expandAct: expandAct,
                expanded: expanded
            }

            //populate project weeks if not already
            console.log('set defaults', cSizeEst, bRateEst, hPerWeekEst, sDate, eDate)
            if (cSizeEst && bRateEst && hPerWeekEst && sDate && eDate) {
                Object.keys(uniqueWeeks).forEach((week) => {
                    const currentWeekData = uData[rowId + '=' + week] || {};
                    const isZeroValues =
                        (!currentWeekData.Rate || Number(currentWeekData.Rate) === 0) &&
                        (!currentWeekData.Number_Hours || Number(currentWeekData.Number_Hours) === 0) &&
                        (!currentWeekData.Number_Units || Number(currentWeekData.Number_Units) === 0);

                    if (isZeroValues) {
                        const dataBurnRate = {
                            active: 1,
                            userId: '154045',
                            userName: 'Seth Bailey',
                            fieldName: 'row',
                            tab: "Burn Tool Labor Phased",
                            rowId: rowId + '=' + week,
                            version: selectedVersion + '',
                            value: uData[rowId + '=' + week]?.burnRateEst ? uData[rowId + '=' + week].burnRateEst : bRateEst + '',
                            id: selectedJob,
                            costCode: rowId,
                            costType: '51100',
                            lockedVersion: lockedVersion,
                            projectStart: startDate,
                            projectEnd: completionDateForecast,
                            phaseStart: sDate,
                            phaseEnd: eDate,
                            rate: bRateEst + '',
                            numberHours: hPerWeekEst + '',
                            numberUnits: cSizeEst + ''
                        };
                        rows.push(dataBurnRate);

                        uData[rowId + '=' + week] = {
                            Rate: uData[rowId + '=' + week]?.burnRateEst ? uData[rowId + '=' + week].burnRateEst : bRateEst + '',
                            Number_Hours: uData[rowId + '=' + week]?.hoursWeek ? uData[rowId + '=' + week].hoursWeek : hPerWeekEst + '',
                            Number_Units: uData[rowId + '=' + week]?.crewSize ? uData[rowId + '=' + week].crewSize : cSizeEst + ''
                        };
                    }
                });
            }

            //if (fieldsThatTriggerCalculation[key]) {
                //hide days that aren't in the range
                projectWeeks.forEach((week) => {
                    console.log('in hide days area', lockedVersion)
                    if (!uniqueWeeks[week] && moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).add(-1, 'week'), 'week')) {
                        console.log('in inner hide area')
                        const dataUpdate = {
                            userId: '154045',
                            userName: 'Seth Bailey',
                            fieldName: 'row',
                            tab: "Burn Tool Labor Phased",
                            rowId: rowId + '=' + week,// + '=default',
                            version: selectedVersion + '',
                            value: '',
                            id: selectedJob,
                            costCode: rowId,
                            costType: '51100',
                            lockedVersion: lockedVersion,
                            projectStart: startDate,
                            projectEnd: completionDateForecast,
                            phaseStart: sDate,
                            phaseEnd: eDate,
                            rate: 0,
                            numberHours: 0,
                            numberUnits: 0
                        }
                        if (uData[rowId + '=' + week]?.Number_Hours || uData[rowId + '=' + week]?.Rate || uData[rowId + '=' + week]?.Number_Units) {
                            rows.push(dataUpdate);
                        }
                        uData[rowId + '=' + week] = {
                            Rate: '',
                            Number_Hours: '',
                            Number_Units: ''
                        }
                    }
                    uData[rowId] = {
                        burnRateTopEst: bRateEst,
                        hrsPerWeekEst: hPerWeekEst,
                        crewSizeEst: cSizeEst,
                        startDate: sDate,
                        endDate: eDate,
                        expandEst: expandEst,
                        expandAct: expandAct,
                        expanded: expanded
                    }
                })
                updateUserDataArray(rows)

                if (cSizeEst && bRateEst && hPerWeekEst && sDate && eDate) {
                    //setUserData(uData)
                    setData(uData[rowId])
                    setDataRows(uData)
                }
            //}
        }

        useEffect(() => {
            setEstAtCompletionHours(fNum(pNum(phaseTotalHours) + pNum(estHours), 1))
            //if(rowId == '100101')console.log('estHours', estHours, phaseTotalHours, phaseBudgetHours)
            setOverUnderHours(fNum((pNum(phaseTotalHours) + pNum(estHours)) - pNum(phaseBudgetHours), 1))
        }, [estHours, phaseTotalHours, phaseBudgetHours])

        useEffect(() => {
            setEstAtCompletionDollars(fNum(pNum(phaseTotalDollars) + pNum(estDollars), 1))
            //if(rowId == '100101')console.log('estDollars', estDollars, phaseTotalDollars, phaseBudgetDollars)
            setOverUnderDollars(fNum((pNum(phaseTotalDollars) + pNum(estDollars)) - pNum(phaseBudgetDollars), 1))
        }, [estDollars, phaseTotalDollars, phaseBudgetDollars])

        useEffect(() => {
            setCode(props.phase?.code)
            setCodeDescription(props.phase?.description)

            let phaseActualHours = {}
            let phaseActualDollars = {}
            let phaseActualCrewCount = {}
            let phaseActualCrew = {}
            let totalActualHours = 0
            let totalActualDollars = 0
            const supplierByWeek = {} // { week: { supplierName: hours } }
            const supplierNameSet = new Set()
            const classificationByWeek = {} // { week: { classification: hours } }
            const classificationSet = new Set()
            const classPeopleByWeek = {} // { classification: { week: { personName: hours } } }
            const classPeopleNameSet = {} // { classification: { personName: staffTitle } }
            const rows = phases[props.phase?.code] ? phases[props.phase?.code] : []
            rows.forEach((row) => {
                const date = moment(row.Consolidated_Date).add(12, 'hours').format('YYYY-MM-DD')
                const week = projectDates[date]
                if (row.Cost_Type_Code[0] == '5' && row.Cost_Type_Code[1] == '1' && row.Cost_Type_Code != '51800') {
                    totalActualDollars += (isNaN(pNum(row.Job_Cost)) ? 0 : pNum(row.Job_Cost))
                    totalActualHours += (isNaN(pNum(row.Total_Hours)) ? 0 : pNum(row.Total_Hours))

                    if (phaseActualDollars[week]) {
                        phaseActualDollars[week] = phaseActualDollars[week] + (isNaN(pNum(row.Job_Cost)) ? 0 : pNum(row.Job_Cost))
                    } else {
                        phaseActualDollars[week] = isNaN(pNum(row.Job_Cost)) ? 0 : pNum(row.Job_Cost)
                    }

                    if (phaseActualHours[week]) {
                        phaseActualHours[week] = phaseActualHours[week] + pNum(row.Total_Hours)
                    } else {
                        phaseActualHours[week] = pNum(row.Total_Hours)
                    }

                    if (phaseActualCrew[week] && !phaseActualCrew[week][row.Staff_ID]) {
                        phaseActualCrew[week][row.Staff_ID] = 1
                        phaseActualCrewCount[week] = phaseActualCrewCount[week] + 1
                    } else if (!phaseActualCrew[week]) {
                        phaseActualCrew[week] = {}
                        phaseActualCrewCount[week] = 1
                        phaseActualCrew[week][row.Staff_ID] = 1
                    }

                    // Track hours by staff name per week
                    const supplier = row.Staff_First_Last_Name || row.Staff_ID || 'Unknown'
                    if (pNum(row.Total_Hours)) {
                        supplierNameSet.add(supplier)
                        if (!supplierByWeek[week]) supplierByWeek[week] = {}
                        supplierByWeek[week][supplier] = (supplierByWeek[week][supplier] || 0) + pNum(row.Total_Hours)
                    }

                    // Track hours by labor classification per week
                    const classification = LaborInterface[row.Labor_Classification_Description] || 'OTHER'
                    if (pNum(row.Total_Hours)) {
                        classificationSet.add(classification)
                        if (!classificationByWeek[week]) classificationByWeek[week] = {}
                        classificationByWeek[week][classification] = (classificationByWeek[week][classification] || 0) + pNum(row.Total_Hours)

                        // Track individual people within each classification
                        if (!classPeopleByWeek[classification]) classPeopleByWeek[classification] = {}
                        if (!classPeopleByWeek[classification][week]) classPeopleByWeek[classification][week] = {}
                        classPeopleByWeek[classification][week][supplier] = (classPeopleByWeek[classification][week][supplier] || 0) + pNum(row.Total_Hours)
                        classPeopleNameSet[classification] = classPeopleNameSet[classification] || {}
                        classPeopleNameSet[classification][supplier] = row.Labor_Classification_Description || ''
                    }
                }
            })
            setSupplierHoursByWeek(supplierByWeek)
            setSupplierNames([...supplierNameSet].sort())
            setClassificationHoursByWeek(classificationByWeek)
            const sortedClassNames = [...classificationSet].sort((a, b) => {
                const order = ['CRAFT', 'FOREMAN', 'SUPERINTENDENT', 'ENGINEER', 'PM', 'CM', 'HSE', 'ADMIN', 'QUALITY', 'ESTIMATION', 'PROCUREMENT', 'MANAGEMENT', 'PROJECT COORDINATOR', 'LOGISTICS', 'BD', 'OTHER']
                return (order.indexOf(a) === -1 ? 99 : order.indexOf(a)) - (order.indexOf(b) === -1 ? 99 : order.indexOf(b))
            })
            setClassificationNames(sortedClassNames)
            setClassificationPeopleByWeek(classPeopleByWeek)
            const classPeopleNamesSorted = {}
            Object.keys(classPeopleNameSet).forEach(cls => {
                classPeopleNamesSorted[cls] = Object.keys(classPeopleNameSet[cls]).sort().map(name => ({ name, title: classPeopleNameSet[cls][name] }))
            })
            setClassificationPeopleNames(classPeopleNamesSorted)


            let etcHours = 0
            let etcDollars = 0
            projectWeeks.forEach((week) => {
                const rowId = props.phase?.code
                const w = dataRows[props.phase?.code + '=' + week]
                if (w && moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(completionDateForecast))) {
                    // no burn rate = zero est hours, same rule as calculateTotals
                    const h = pNum(w.Rate) > 0 ? (w.Number_Hours ? w.Number_Hours : w.hoursPerWeekEst ? w.hoursPerWeekEst : 0) : 0
                    etcHours = etcHours + (!isNaN(pNum(h)) && !isNaN(pNum(w.Number_Units)) ? pNum(h) * pNum(w.Number_Units) : 0)
                    etcDollars = etcDollars + (!isNaN(pNum(h)) && !isNaN(pNum(w.Rate)) && !isNaN(pNum(w.Number_Units)) ? pNum(h) * pNum(w.Rate) * pNum(w.Number_Units) : 0)
                }
            })
            setEstDollars(fNum(etcDollars, 1))
            setEstHours(fNum(etcHours, 1))

            const rowsBudget = phasesBudget[props.phase?.code] ? phasesBudget[props.phase?.code] : []
            let totalBudgetHours = 0
            let totalBudgetDollars = 0
            rowsBudget.forEach((row) => {
                if (row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8') {
                    totalBudgetHours += (isNaN(pNum(row.Hours)) ? 0 : pNum(row.Hours))
                    totalBudgetDollars += (isNaN(pNum(row['Value_Amount'])) ? 0 : pNum(row['Value_Amount']))
                }
            })
            if(props.phase?.code == '160507')console.log('here 160507', totalBudgetDollars, totalBudgetHours, coPhaseTotals[props.phase?.code], coPhaseHourTotals[props.phase?.code])
            totalBudgetDollars = totalBudgetDollars + (isNaN(pNum(coPhaseTotals[props.phase?.code])) ? 0 : pNum(coPhaseTotals[props.phase?.code]))
            totalBudgetHours = totalBudgetHours + (isNaN(pNum(coPhaseHourTotals[props.phase?.code])) ? 0 : pNum(coPhaseHourTotals[props.phase?.code]))

            setPhaseBudgetDollars(totalBudgetDollars)
            setPhaseBudgetHours(totalBudgetHours)
            setPhaseActualDollars(phaseActualDollars)
            setPhaseActualHours(phaseActualHours)
            setPhaseActualCrewCount(phaseActualCrewCount)
            setPhaseTotalHours(totalActualHours)
            setPhaseTotalDollars(totalActualDollars)
        }, [dataRows])

        const WeekValue = (props) => {
            //if(props.week == '2025-05-05') console.log('here seth', props)
            const [user, setUser] = useState(props.data)
            const week = props.week
            // WeekValue is defined inline inside BurnToolPhased, so any parent
            // re-render unmounts and remounts every cell. Without this, useState
            // would re-init from props.data (= dataRows[...]) — which never
            // reflects the user's in-flight typed value until a server reload.
            // Seed from a parent-level ref first so the user's typed value
            // survives parent re-renders (e.g. an API tracker update, an atom
            // change, a sibling phase rendering).
            const weekKey = (typeof rowId !== 'undefined' ? rowId : '') + '=' + week
            const seeded = userWeekInputRef.current[weekKey] || {}
            const [hoursWeek, setHoursWeek] = useState(
                seeded.hoursWeek !== undefined ? f(pNum(seeded.hoursWeek))
                : user?.Number_Hours ? f(user?.Number_Hours)
                : user?.hoursPerWeekEst ? f(user?.hoursPerWeekEst)
                : 0
            )
            const [crewSize, setCrewSize] = useState(
                seeded.crewSize !== undefined ? f(pNum(seeded.crewSize))
                : user?.Number_Units ? f(pNum(user?.Number_Units))
                : 0
            )
            const [estHours, setEstHours] = useState()
            const [burnRateEst, setBurnRateEst] = useState(
                seeded.rate !== undefined ? f(pNum(seeded.rate))
                : user?.Rate ? f(pNum(user?.Rate))
                : 0
            )
            const [estWeekCost, setEstWeekCost] = useState(0)
            const beforeToday = moment(versionInfo.Version_Start_DateTime).isAfter(moment(week).add(7, 'days'))
            const isThisWeek = moment(week).add(7, 'days').isSame(moment(versionInfo.Version_Start_DateTime), 'week')
            const isBeforeThisWeek = moment(week).isBefore(moment(versionInfo.Version_Start_DateTime), 'week')
            const isBetweenInterval = startDate && endDate ? moment(week).isBetween(moment(startDate).startOf('isoWeek').add(-1, 'day'), moment(endDate).add(1, 'day')) : false
            const changeListRefWeek = useRef([]);

            useEffect(() => {
                if (!isNaN(hoursWeek?.floatValue) && !isNaN(crewSize?.floatValue) && !isNaN(burnRateEst?.floatValue)) {
                    setEstWeekCost(fNum(burnRateEst.floatValue * hoursWeek.floatValue * crewSize.floatValue, 1))
                    setEstHours(fNum(hoursWeek?.floatValue * crewSize?.floatValue, 1))
                }
            }, [hoursWeek, crewSize, burnRateEst])

            const debounceSaveWeek = useCallback(
                debounce(() => {
                    const uniqueRows = {};
                    changeListRefWeek.current.forEach(v => {
                        uniqueRows[v.row] = v;
                    });
                    const lastValues = Object.values(uniqueRows);
                    console.log('lastValues', lastValues, changeListRefWeek.current);
                    saveValuesWeek(lastValues);
                    //saveValuesWeek(changeListRefWeek.current);
                    changeListRefWeek.current = [];
                }, 2000),
                []
            );

            const handleInputChangeWeek = (v) => {
                changeListRefWeek.current.push(v);
                debounceSaveWeek();
            };

            //seth
            const saveValuesWeek = (values) => {
                //console.log('save values week', values)
                values.forEach((v) => {
                    const numOfWorkers = v.crewSize ? v.crewSize : user?.crewSize ? user.crewSize : 0
                    const hoursPerWeek = v.hoursWeek ? v.hoursWeek : user?.hoursWeek ? user.hoursWeek : 0
                    const burnPerHour = v.rate ? v.rate : user?.burnRateEst ? user.burnRateEst : 0
                    //console.log('crewSize', crewSize, 'hoursWeek', hoursWeek, 'burnRateEst', burnRateEst)
                    const notes = v.key == 'notes' ? v.value : ''
                    const completionDateForecast = v.key == 'completionDateForecast' ? v.value : ''
                    let additionalData = {
                        active: 1,
                        userId: '154045',
                        userName: 'Seth Bailey',
                        fieldName: 'row',
                        tab: "Burn Tool Labor Phased",
                        rowId: v.row,
                        costCode: v.row ? v.row.split('=')[0] : '',
                        version: selectedVersion + '',
                        value: pNum(numOfWorkers) * pNum(hoursPerWeek) * pNum(burnPerHour),
                        valueAmount: pNum(numOfWorkers) * pNum(hoursPerWeek) * pNum(burnPerHour),
                        notes: notes,
                        projectStart: selectedJobData?.Planned_Start_Date,
                        projectFinish: completionDateForecast ? completionDateForecast : selectedJobData?.Planned_Complete_Date,
                        // phaseStart: selectedJobData?.Phase_Start_Date ? moment(selectedJobData?.Phase_Start_Date).add(12, 'hours').toISOString() : '',
                        // phaseFinish: completionDateForecast ? moment(completionDateForecast).add(12, 'hours').toISOString() : selectedJobData?.Phase_Complete_Date ? moment(selectedJobData?.Phase_Complete_Date).add(12, 'hours').toISOString() : '',
                        phaseStart: selectedJobData?.Phase_Start_Date,
                        phaseFinish: completionDateForecast ? completionDateForecast : selectedJobData?.Phase_Complete_Date,
                        rate: burnPerHour,
                        numberHours: hoursPerWeek,
                        numberUnits: numOfWorkers,
                        hours: numOfWorkers * hoursPerWeek,
                        active: 1,
                        id: selectedJob,
                        costType: '51100',
                        lockedVersion: lockedVersion
                    };
                    console.log('additionalData', additionalData)
                    updateUserDataArray(additionalData);
                })
            }

            const setDefaults = () => {
                //console.log('crewSizeEst', crewSizeEst, 'hrsPerWeekEst', hrsPerWeekEst, 'burnRateTopEst', burnRateTopEst)
                const user = {
                    active: 1,
                    userId: '154045',
                    userName: 'Seth Bailey',
                    fieldName: 'row',
                    tab: "Burn Tool Labor Phased",
                    rowId: rowId + '=' + week,
                    costCode: rowId,
                    version: selectedVersion + '',
                    value: pNum(crewSizeEst) * pNum(hrsPerWeekEst) * pNum(burnRateTopEst),
                    valueAmount: pNum(crewSizeEst) * pNum(hrsPerWeekEst) * pNum(burnRateTopEst),
                    notes: '',
                    projectStart: selectedJobData?.Planned_Start_Date,
                    projectFinish: completionDateForecast ? completionDateForecast : selectedJobData?.Planned_Complete_Date,
                    phaseStart: selectedJobData?.Phase_Start_Date,
                    phaseFinish: completionDateForecast ? completionDateForecast : selectedJobData?.Phase_Complete_Date,
                    rate: burnRateTopEst,
                    numberHours: hrsPerWeekEst,
                    numberUnits: crewSizeEst,
                    hours: pNum(crewSizeEst) * pNum(hrsPerWeekEst),
                    active: 1,
                    id: selectedJob,
                    costType: '51100',
                    lockedVersion: lockedVersion
                };
                //console.log('setting defaults', user)
                updateUserDataArray(user);

                setData({
                    burnRateEst: burnRateTopEst,
                    hoursWeek: hrsPerWeekEst,
                    crewSize: crewSizeEst
                })
                setBurnRateEst(fNum(burnRateTopEst))
                setHoursWeek(fNum(hrsPerWeekEst, 1))
                setCrewSize(fNum(crewSizeEst))
                userWeekInputRef.current[weekKey] = {
                    rate: pNum(burnRateTopEst),
                    hoursWeek: pNum(hrsPerWeekEst),
                    crewSize: pNum(crewSizeEst),
                }
            }

            return (
                <View style={{ borderRight: isThisWeek ? '2px solid #1e40af' : 'none' }}>
                    <Text style={[s.rateC, { backgroundColor: isBetweenInterval ? '#dbeafe' : '#f8fafc', paddingBottom: 3, borderBottom: '2px solid #94a3b8', height: 24 }]}>{week}</Text>
                    <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{estWeekCost}
                    </Text>
                    {expanded && expandEst ? <View>
                        {!beforeToday && isBetweenInterval ?
                            <View style={{ position: 'relative' }}>
                                <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }}></View>
                                <Pressable
                                    tabIndex={4}
                                    style={{ borderLeft: '10px solid transparent', borderRight: '10px solid #94a3b8', borderBottom: '10px solid transparent', right: 0, top: 0, position: 'absolute' }}
                                    onPress={setDefaults}>
                                </Pressable>
                                <NumberInput style={{ ...s.rate, border: '1px solid #e2e5ea', borderRight: '1px solid #d5d9e0', paddingLeft: 6 }}
                                    thousandSeparator
                                    decimalSeparator="."
                                    tabIndex={1}
                                    valueIsNumericString={true}
                                    value={fNum(burnRateEst?.formattedValue)}
                                    onValueChange={(t) => {
                                        setBurnRateEst(t)
                                        userWeekInputRef.current[weekKey] = { ...(userWeekInputRef.current[weekKey] || {}), rate: t.floatValue }
                                        handleInputChangeWeek({ rate: t.floatValue, hoursWeek: hoursWeek?.formattedValue, crewSize: crewSize?.formattedValue, key: 'burnRateEst', row: rowId + '=' + week })
                                    }}
                                />
                            </View> :
                            <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{fNum(pNum(burnRateEst?.formattedValue))}</Text>//seth
                        }
                        {!beforeToday && isBetweenInterval ?
                            <View style={{ position: 'relative' }}>
                                <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }}></View>
                                <NumberInput style={{ ...s.rate, border: '1px solid #e2e5ea', borderRight: '1px solid #d5d9e0', paddingLeft: 6 }}
                                    thousandSeparator
                                    decimalSeparator="."
                                    tabIndex={3}
                                    valueIsNumericString={true}
                                    value={fNum(hoursWeek?.formattedValue, 1)}
                                    onValueChange={(t) => {
                                        setHoursWeek(t)
                                        userWeekInputRef.current[weekKey] = { ...(userWeekInputRef.current[weekKey] || {}), hoursWeek: t.floatValue }
                                        handleInputChangeWeek({ rate: burnRateEst?.formattedValue, hoursWeek: t.floatValue, crewSize: crewSize?.formattedValue, key: 'hoursWeek', row: rowId + '=' + week })
                                    }}
                                />
                            </View> :
                            <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{hoursWeek?.formattedValue}</Text>

                        }
                        {!beforeToday && isBetweenInterval ?
                            <View style={{ position: 'relative' }}>
                                <View style={{ width: 3, height: '100%', background: 'linear-gradient(to bottom, #d1d5db, #9ca3af)', left: 0, top: 0, position: 'absolute' }}></View>
                                <NumberInput style={{ ...s.rate, border: '1px solid #e2e5ea', borderRight: '1px solid #d5d9e0', paddingLeft: 6 }}
                                    thousandSeparator
                                    decimalSeparator="."
                                    tabIndex={2}
                                    valueIsNumericString={true}
                                    value={fNum(crewSize?.formattedValue)}
                                    onValueChange={(t) => {
                                        setCrewSize(t)
                                        userWeekInputRef.current[weekKey] = { ...(userWeekInputRef.current[weekKey] || {}), crewSize: t.floatValue }
                                        handleInputChangeWeek({ rate: burnRateEst?.formattedValue, hoursWeek: hoursWeek?.formattedValue, crewSize: t.floatValue, key: 'crewSize', row: rowId + '=' + week })
                                    }}
                                />
                            </View> :
                            <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{fNum(pNum(crewSize?.formattedValue))}</Text>
                        }
                    </View> : null}
                    <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white', borderTopWidth: 2, borderTopColor: '#1e40af' }]}>{!isNaN(phaseActualDollars[week]) ? fNum(phaseActualDollars[week], 1) : ''}</Text>
                    {expanded && expandAct ? <View>
                        <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{phaseActualDollars[week] && phaseActualHours[week] ? fNum(phaseActualDollars[week] / phaseActualHours[week], 1) : ''}</Text>
                        <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{!isNaN(phaseActualHours[week]) ? fNum(phaseActualHours[week] / phaseActualCrewCount[week], 1) : ''}</Text>
                        <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{!isNaN(phaseActualCrewCount[week]) ? fNum(phaseActualCrewCount[week]) : ''}</Text>
                    </View> : null}
                    {(() => {
                        let cumEst = 0
                        projectWeeks.forEach(w => {
                            const d = dataRows[rowId + '=' + w]
                            if (d) {
                                const h = pNum(d.Rate) > 0 ? (d.Number_Hours ? d.Number_Hours : d.hoursPerWeekEst ? d.hoursPerWeekEst : 0) : 0
                                const wHrs = !isNaN(pNum(h)) && !isNaN(pNum(d.Number_Units)) ? pNum(h) * pNum(d.Number_Units) : 0
                                if (w <= week) cumEst += wHrs
                            }
                        })
                        const budgetHrs = pNum(phaseBudgetHours)
                        const estPct = budgetHrs > 0 ? Math.round((cumEst / budgetHrs) * 100) : 0
                        return (
                            <View style={{ position: 'relative' }}>
                                <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white', borderTopWidth: 2, borderTopColor: '#1e40af' }]}>{estHours}</Text>
                                {estHours && budgetHrs > 0 ? <Text style={{ position: 'absolute', right: 1, bottom: 0, fontSize: 7, color: '#64748b', fontWeight: '600' }}>{estPct}%</Text> : null}
                            </View>
                        )
                    })()}
                    {(() => {
                        const actHrs = !isNaN(phaseActualHours[week]) ? phaseActualHours[week] : 0
                        let cumAct = 0
                        projectWeeks.forEach(w => {
                            const a = !isNaN(phaseActualHours[w]) ? phaseActualHours[w] : 0
                            if (w <= week) cumAct += a
                        })
                        const budgetHrsAct = pNum(phaseBudgetHours)
                        const actPct = budgetHrsAct > 0 ? Math.round((cumAct / budgetHrsAct) * 100) : 0
                        return (
                            <View style={{ position: 'relative' }}>
                                <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{actHrs ? fNum(actHrs, 1) : ''}</Text>
                                {actHrs > 0 && budgetHrsAct > 0 ? <Text style={{ position: 'absolute', right: 1, bottom: 0, fontSize: 7, color: '#64748b', fontWeight: '600' }}>{actPct}%</Text> : null}
                            </View>
                        )
                    })()}
                    {expandClassificationHours && classificationNames.length > 0 ? <View>
                        {classificationNames.map((cls, idx) => {
                            const clsHrs = classificationHoursByWeek[week]?.[cls]
                            return <View key={idx}>
                                <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f0f4ff' : '#f5f8ff', fontSize: 9, fontWeight: '600' }]}>{clsHrs ? fNum(clsHrs, 1) : ''}</Text>
                                {expandedClassifications[cls] && classificationPeopleNames[cls]?.length > 0 ? 
                                    classificationPeopleNames[cls].map((item, pIdx) => {
                                        const personHrs = classificationPeopleByWeek[cls]?.[week]?.[item.name]
                                        return <Text key={pIdx} style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f5f5f5' : '#fafafa', fontSize: 8 }]}>{personHrs ? fNum(personHrs, 1) : ''}</Text>
                                    })
                                : null}
                            </View>
                        })}
                    </View> : null}
                    {(() => {
                        const plannedHrs = phaseHoursData[rowId]?.[week] ? pNum(phaseHoursData[rowId][week]) : 0
                        const phaseWeeks = phaseHoursData[rowId] || {}
                        let cumPlanned = 0
                        Object.keys(phaseWeeks).sort().forEach(w => {
                            if (w <= week) cumPlanned += pNum(phaseWeeks[w]) || 0
                        })
                        const budgetHrsPlanned = pNum(phaseBudgetHours)
                        const pct = budgetHrsPlanned > 0 ? Math.round((cumPlanned / budgetHrsPlanned) * 100) : 0
                        return (
                            <View style={{ position: 'relative' }}>
                                <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{plannedHrs ? fNum(plannedHrs, 1) : ''}</Text>
                                {plannedHrs > 0 && budgetHrsPlanned > 0 ? <Text style={{ position: 'absolute', right: 1, bottom: 0, fontSize: 7, color: pct >= 100 ? '#dc2626' : '#64748b', fontWeight: '600' }}>{pct}%</Text> : null}
                            </View>
                        )
                    })()}
                    {guardAdmin && isLiveVersion && <Text style={[s.rate, { backgroundColor: isBeforeThisWeek ? '#f9fafb' : 'white' }]}>{phaseHeetData[week] ? fNum(phaseHeetData[week], 1) : ''}</Text>}
                </View>
            )
        }

        //need to change
        const setDefaultsAcrossWeeks = () => {
            console.log('setting defaults')
            let uniqueWeeks = {}

            const plannedStart = moment(startDate)
            const plannedFinish = moment(endDate)
            let currentDay = plannedStart
            let currentWeek = plannedStart
            const days = plannedFinish.diff(plannedStart, 'days')
            const lastWeekBeforeForecast = moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('week');
            for (let i = 0; i < days + 1; i++) {
                const day = currentDay.clone()
                const week = currentDay.clone()
                day.add(i, 'days')
                week.add(i, 'days')
                currentWeek = week.startOf('isoWeek')
                if (moment(currentWeek).isAfter(lastWeekBeforeForecast, 'week')) {
                    uniqueWeeks[currentWeek.format('YYYY-MM-DD')] = 1
                }
            }

            let uData = JSON.parse(JSON.stringify(dataRows))
            // One request for the whole spread (see saveValue).
            const rows = []

            Object.keys(uniqueWeeks).forEach((week, index) => {
                //console.log('week', week)
                const dataCrewSize = {
                    active: 1,
                    userId: '154045',
                    userName: 'Seth Bailey',
                    fieldName: 'row',
                    tab: "Burn Tool Labor Phased",
                    rowId: rowId + '=' + week,// + '=default',
                    version: selectedVersion + '',
                    value: crewSizeEst,
                    id: selectedJob,
                    costCode: rowId,
                    costType: '51100',
                    numberUnits: crewSizeEst,
                    numberHours: uData[rowId + '=' + week]?.hoursWeek ? uData[rowId + '=' + week].hoursWeek : hrsPerWeekEst,
                    rate: uData[rowId + '=' + week]?.burnRateEst ? uData[rowId + '=' + week].burnRateEst : burnRateTopEst,
                    lockedVersion: lockedVersion
                }
                rows.push(dataCrewSize)

                uData[rowId + '=' + week] = { ...(uData[rowId + '=' + week] || {}), Number_Units: crewSizeEst }
                //if(index == 0) console.log('first week', week, rowId, dataRows, uData[rowId + '=' + week], dataCrewSize)
            })
            if (rows.length) updateUserDataArray(rows)

            //setUserData(uData)
            setData(uData[rowId])
            setDataRows(uData)
        }

        //need to change
        const setDefaultsAcrossWeeksHrs = () => {
            let uniqueWeeks = {}

            const plannedStart = moment(startDate)
            const plannedFinish = moment(endDate)
            let currentDay = plannedStart
            let currentWeek = plannedStart
            const days = plannedFinish.diff(plannedStart, 'days')
            const lastWeekBeforeForecast = moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('week');
            for (let i = 0; i < days + 1; i++) {
                const day = currentDay.clone()
                const week = currentDay.clone()
                day.add(i, 'days')
                week.add(i, 'days')
                currentWeek = week.startOf('isoWeek')
                if (moment(currentWeek).isAfter(lastWeekBeforeForecast, 'week')) {
                    uniqueWeeks[currentWeek.format('YYYY-MM-DD')] = 1
                }
            }

            let uData = JSON.parse(JSON.stringify(dataRows))
            const rows = []
            Object.keys(uniqueWeeks).forEach((week) => {
                const dataHoursWeek = {
                    active: 1,
                    userId: '154045',
                    userName: 'Seth Bailey',
                    fieldName: 'row',
                    tab: "Burn Tool Labor Phased",
                    rowId: rowId + '=' + week,// + '=default',
                    version: selectedVersion + '',
                    value: hrsPerWeekEst,
                    id: selectedJob,
                    costCode: rowId,
                    costType: '51100',
                    numberUnits: uData[rowId + '=' + week]?.crewSize ? uData[rowId + '=' + week].crewSize : crewSizeEst,
                    numberHours: hrsPerWeekEst,
                    rate: uData[rowId + '=' + week]?.burnRateEst ? uData[rowId + '=' + week].burnRateEst : burnRateTopEst,
                    lockedVersion: lockedVersion
                }
                rows.push(dataHoursWeek)

                uData[rowId + '=' + week] = { ...(uData[rowId + '=' + week] || {}), Number_Hours: hrsPerWeekEst }
            })
            if (rows.length) updateUserDataArray(rows)

            //setUserData(uData)
            setData(uData[rowId])
            setDataRows(uData)
        }

        //need to change
        const setDefaultsAcrossWeeksBurnRate = () => {
            let uniqueWeeks = {}

            const plannedStart = moment(startDate)
            const plannedFinish = moment(endDate)
            let currentDay = plannedStart
            let currentWeek = plannedStart
            const days = plannedFinish.diff(plannedStart, 'days')
            const lastWeekBeforeForecast = moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('week');
            for (let i = 0; i < days + 1; i++) {
                const day = currentDay.clone()
                const week = currentDay.clone()
                day.add(i, 'days')
                week.add(i, 'days')
                currentWeek = week.startOf('isoWeek')
                if (moment(currentWeek).isAfter(lastWeekBeforeForecast, 'week')) {
                    uniqueWeeks[currentWeek.format('YYYY-MM-DD')] = 1
                }
            }

            let uData = JSON.parse(JSON.stringify(dataRows))
            const rows = []
            Object.keys(uniqueWeeks).forEach((week) => {
                // Triangle on Burn Rate → push burn rate only. Preserve any
                // per-week crew/hours overrides exactly; for weeks that never
                // had explicit values, send empty so the read path keeps
                // inheriting the top-row default (instead of materializing the
                // current top values into every future week).
                const existing = uData[rowId + '=' + week] || {};
                const dataBurnRate = {
                    active: 1,
                    userId: '154045',
                    userName: 'Seth Bailey',
                    fieldName: 'row',
                    tab: "Burn Tool Labor Phased",
                    rowId: rowId + '=' + week,// + '=default',
                    version: selectedVersion + '',
                    value: burnRateTopEst,
                    id: selectedJob,
                    costCode: rowId,
                    costType: '51100',
                    numberUnits: existing.crewSize ? existing.crewSize : '',
                    numberHours: existing.hoursWeek ? existing.hoursWeek : '',
                    rate: burnRateTopEst,
                    lockedVersion: lockedVersion
                }
                rows.push(dataBurnRate)
                uData[rowId + '=' + week] = { ...(uData[rowId + '=' + week] || {}), Rate: burnRateTopEst }
            })
            if (rows.length) updateUserDataArray(rows)
            //setUserData(uData)
            setData(uData[rowId])
            setDataRows(uData)
        }

        const handleDebouncedChange = debounce((burnRate, hoursWeek, crewSize, startDate, endDate) => {
            saveValue(burnRate, hoursWeek, crewSize, startDate, endDate)
        }, 500); // 500

        useEffect(() => {
            setBurnRateBudget(phaseBudgetDollars && phaseBudgetHours && pNum(phaseBudgetHours) > 0 ? fNum(pNum(phaseBudgetDollars) / pNum(phaseBudgetHours)) : '')
        }, [phaseBudgetDollars, phaseBudgetHours])

        useEffect(() => {
            setBurnRateActual(phaseTotalDollars && phaseTotalHours && pNum(phaseTotalHours) > 0 ? fNum(pNum(phaseTotalDollars) / pNum(phaseTotalHours)) : '')
        }, [phaseTotalDollars, phaseTotalHours])


        useEffect(() => {
            setBurnRateEtc(estHours && pNum(estHours) > 0 ? fNum(pNum(estDollars) / pNum(estHours)) : '')
        }, [estDollars, estHours])

        useEffect(() => {
            setBurnRateAtCompletion(estAtCompletionHours && pNum(estAtCompletionHours) > 0 ? fNum(pNum(estAtCompletionDollars) / pNum(estAtCompletionHours)) : '')
        }, [estAtCompletionDollars, estAtCompletionHours])

        useEffect(() => {
            setBurnRateOverUnder(phaseBudgetHours && estAtCompletionHours && pNum(phaseBudgetHours) > 0 ? fNum((pNum(estAtCompletionDollars) / pNum(estAtCompletionHours)) - (pNum(phaseBudgetDollars) / pNum(phaseBudgetHours))) : '')
        }, [estAtCompletionDollars, estAtCompletionHours, phaseBudgetDollars, phaseBudgetHours])

        // Trending EAC hours — same formula as the Productivity tab (productivityCombined PhaseRow):
        // past 25% complete, actual hrs per installed qty x total qty; at 100%, actual hours; before 25%, budget hours.
        // Installed = HeavyJob FP quantities + scorecard installed; total qty = Day Zero + approved CO + scorecard plug.
        const trendEACHours = (() => {
            const hji = pNum(heavyJobInstalledByPhase[rowId]) || 0
            const inst = scorecardData[rowId]?.installed ? pNum(scorecardData[rowId].installed) : 0
            const totalInst = (isNaN(hji) ? 0 : hji) + (isNaN(inst) ? 0 : inst)
            const dzQty = pNum(dayZeroPhaseQuantities[rowId]) || 0
            const coQty = pNum(coPhaseQuantities[rowId]) || 0
            const plug = scorecardData[rowId]?.plug ? pNum(scorecardData[rowId].plug) : 0
            const totalQty = dzQty + coQty + (isNaN(plug) ? 0 : plug)
            const budgetHrs = pNum(phaseBudgetHours) || 0
            const actualHrs = pNum(phaseTotalHours) || 0
            if (totalQty <= 0) return budgetHrs > 0 || actualHrs > 0 ? fNum(Math.max(budgetHrs, actualHrs), 1) : ''
            const pct = 100 * totalInst / totalQty
            if (pct >= 100) return fNum(actualHrs, 1)
            if (pct > 25 && totalInst > 0) return fNum((actualHrs / totalInst) * totalQty, 1)
            return fNum(budgetHrs, 1)
        })()

        return (
            <View style={{ backgroundColor: 'white', flexDirection: 'row', marginBottom: 2 }}>
                <View style={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 10, flexDirection: 'row' }}>
                    <View>
                        <Pressable
                            onPress={props.onCollapse}
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                paddingVertical: 4,
                                paddingHorizontal: 0,
                                backgroundColor: 'white',
                                borderRadius: 4,
                            }}
                        >
                            <View style={{ flexDirection: 'row', alignItems: 'center', width: 18, justifyContent: 'center' }}>
                                <Icon3
                                    name="MdExpandMore"
                                    size={14}
                                    color="#475569"
                                />
                            </View>
                            <Text style={{ fontSize: 11, fontWeight: '700', color: '#1e293b' }}>{code}</Text>
                            <Text numberOfLines={1} style={{ fontSize: 11, color: '#64748b', marginLeft: 6, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{codeDescription}</Text>
                            {(() => {
                                const hji = heavyJobInstalledByPhase[rowId] || 0
                                const inst = scorecardData[rowId]?.installed ? pNum(scorecardData[rowId].installed) : 0
                                const totalInst = (isNaN(pNum(hji)) ? 0 : pNum(hji)) + (isNaN(inst) ? 0 : inst)
                                const dzQty = pNum(dayZeroPhaseQuantities[rowId]) || 0
                                const coQty = pNum(coPhaseQuantities[rowId]) || 0
                                const plug = scorecardData[rowId]?.plug ? pNum(scorecardData[rowId].plug) : 0
                                const totalQty = dzQty + coQty + (isNaN(plug) ? 0 : plug)
                                const pct = totalQty > 0 ? (100 * totalInst / totalQty) : 0
                                return totalQty > 0 ? <Text style={{ fontSize: 11, color: pct >= 100 ? '#16a34a' : '#475569', fontWeight: '600', marginLeft: 8 }}>{fNum(pct, 1)}%</Text> : null
                            })()}
                            <Icon3
                                onPress={(e) => {
                                    e.stopPropagation && e.stopPropagation()
                                    reloadUserData()
                                }}
                                name="MdSync"
                                size={12}
                                color="#64748b"
                                style={{ marginLeft: 6, cursor: 'pointer' }}
                            />
                        </Pressable>
                        <View style={{ zIndex: 20, backgroundColor: 'white', borderRadius: 4, border: '1px solid #e2e8f0', padding: 0, paddingLeft: 5, overflow: 'hidden' }}>
                            {/* Input controls row */}
                            <View style={{ flexDirection: 'row', alignItems: 'stretch', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {[
                                    { label: 'Start Date', type: 'date', width: 100, value: startDate, onChange: (t) => { setStartDate(t); saveValue(burnRateTopEst, hrsPerWeekEst, crewSizeEst, moment(t).format('YYYY-MM-DD'), endDate) }, onBlur: (t) => { saveValue(burnRateTopEst, hrsPerWeekEst, crewSizeEst, moment(t).format('YYYY-MM-DD'), endDate) } },
                                    { label: 'End Date', type: 'date', width: 100, value: endDate, onChange: (t) => { setEndDate(t); saveValue(burnRateTopEst, hrsPerWeekEst, crewSizeEst, startDate, moment(t).format('YYYY-MM-DD')) }, onBlur: (t) => { saveValue(burnRateTopEst, hrsPerWeekEst, crewSizeEst, startDate, moment(t).format('YYYY-MM-DD')) }, warn: endDate && moment(endDate).isAfter(completionDateForecast) },
                                    { label: 'Burn Rate', type: 'text', width: 52, value: burnRateTopEst, onChange: (v) => { handleDebouncedChange(v, hrsPerWeekEst, crewSizeEst, startDate, endDate); setBurnRateEst(v) }, onApply: setDefaultsAcrossWeeksBurnRate },
                                    { label: 'Hrs/Wk', type: 'text', width: 44, value: hrsPerWeekEst, onChange: (v) => { handleDebouncedChange(burnRateTopEst, v, crewSizeEst, startDate, endDate); setHrsPerWeekEst(v) }, onApply: setDefaultsAcrossWeeksHrs },
                                    { label: 'Crew', type: 'text', width: 40, value: crewSizeEst, onChange: (v) => { handleDebouncedChange(burnRateTopEst, hrsPerWeekEst, v, startDate, endDate); setCrewSizeEst(v) }, onApply: setDefaultsAcrossWeeks },
                                ].map((field, idx) => (
                                    <View key={idx} style={{ width: field.width, borderRight: idx < 4 ? '1px solid #e2e8f0' : 'none' }}>
                                        <Text style={{ fontSize: 8, fontWeight: '600', color: '#64748b', textAlign: 'center', paddingVertical: 2, backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: 0.3 }}>{field.label}</Text>
                                        <View style={{ padding: 2, position: 'relative' }}>
                                            {field.onApply && <Pressable
                                                style={{ position: 'absolute', right: 1, top: 1, zIndex: 2, width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid #94a3b8', borderBottom: '6px solid transparent' }}
                                                onPress={field.onApply}
                                            />}
                                            {field.type === 'date' ? (
                                                <DateInput
                                                    value={field.value}
                                                    onValueChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    style={{ width: '100%', height: 22, fontSize: 10, border: '1px solid #e2e5ea', borderRadius: 3, paddingLeft: 2, textAlign: 'center', color: field.warn ? '#dc2626' : '#1f2937' }}
                                                />
                                            ) : (
                                                <TextInput
                                                    style={{ width: '100%', height: 22, fontSize: 10, border: '1px solid #e2e5ea', borderRadius: 3, paddingLeft: 2, textAlign: 'center', color: '#1f2937' }}
                                                    onChangeText={field.onChange}
                                                    value={field.value}
                                                />
                                            )}
                                        </View>
                                    </View>
                                ))}
                            </View>
                            {/* Summary table */}
                            <View style={{ padding: 0 }}>
                                <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9' }}>
                                    <Text style={{ width: 50, fontSize: 10, fontWeight: '600', color: '#64748b', padding: 2, textAlign: 'right' }}></Text>
                                    {['Current\nBudget', 'Actual\nCosts', 'Est. To\nCompl.', 'Est. At\nCompl.', 'Over /\n(Under)', 'Trend\nEAC'].map((h, i) => (
                                        <Text key={i} style={{ width: 52, fontSize: 10, fontWeight: '600', color: '#64748b', padding: 2, textAlign: 'right', borderLeft: '1px solid #e2e8f0' }} title={i === 5 ? 'Trending EAC hours from the Productivity tab: actual hrs per installed qty x total qty once past 25% complete; actual hours at 100%; budget hours before 25%' : undefined}>{h}</Text>
                                    ))}
                                </View>
                                {[
                                    { label: 'Dollars', values: [fNum(pNum(phaseBudgetDollars), 1), fNum(pNum(phaseTotalDollars), 1), fNum(pNum(estDollars), 1), fNum(pNum(estAtCompletionDollars), 1), fNum(pNum(overUnderDollars), 1), ''], bg: 'white' },
                                    { label: 'Hours', values: [fNum(pNum(phaseBudgetHours), 1), fNum(pNum(phaseTotalHours), 1), fNum(pNum(estHours), 1), estAtCompletionHours, overUnderHours, trendEACHours], bg: '#fafbfc', trendWarn: trendEACHours !== '' && pNum(trendEACHours) > pNum(estAtCompletionHours) },
                                    { label: 'Burn Rate', values: [fNum(pNum(burnRateBudget)), fNum(pNum(burnRateActual)), fNum(pNum(burnRateEtc)), burnRateAtCompletion, burnRateOverUnder, ''], bg: 'white' },
                                ].map((row, rIdx) => (
                                    <View key={rIdx} style={{ flexDirection: 'row', backgroundColor: row.bg, borderTop: '1px solid #f0f0f0' }}>
                                        <Text style={{ width: 50, fontSize: 10, fontWeight: '600', color: '#475569', padding: 2, textAlign: 'right', backgroundColor: '#f8fafc' }}>{row.label}</Text>
                                        {row.values.map((v, i) => (
                                            <Text key={i} style={{ width: 52, fontSize: 11, color: (i === 4 && pNum(v) > 0) || (i === 5 && row.trendWarn) ? '#dc2626' : i === 5 ? '#6b7280' : '#1f2937', fontWeight: i === 4 || (i === 5 && row.trendWarn) ? '600' : 'normal', padding: 2, textAlign: 'right', borderLeft: i === 5 ? '1px solid #e2e8f0' : '1px solid #f0f0f0' }}>{v}</Text>
                                        ))}
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                    <View style={{ marginLeft: totalsExpanded ? expanded ? 187 : 388 : 60 }}>
                        <Text style={s.spanSC}></Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Icon3
                                style={{ backgroundColor: '#f3f4f6', cursor: 'pointer' }}
                                onPress={() => {
                                    if (!expandEst && !expanded) {
                                        setExpanded(true)
                                    }
                                    setExpandEst(!expandEst)
                                }}
                                name={expandEst ? "MdExpandMore" : "MdChevronRight"}
                                size={20}
                            />
                            <Text style={[s.spanS, { backgroundColor: '#f3f4f6', fontWeight: '600', width: 120, paddingTop: 2 }]}>
                                Est. Weekly Cost
                            </Text>
                        </View>
                        {expanded && expandEst ? <View>
                            <Text style={[s.span, { backgroundColor: '#f3f4f6', paddingLeft: 24, width: 140 }]}>
                                Burn Rate
                            </Text>
                            <Text style={[s.span, { backgroundColor: '#f3f4f6', paddingLeft: 24, width: 140 }]}>
                                Hours/Week
                            </Text>
                            <Text style={[s.span, { backgroundColor: '#f3f4f6', paddingLeft: 24, width: 140 }]}>
                                Crew Size
                            </Text>
                        </View> : null}
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Icon3
                                style={{ cursor: 'pointer' }}
                                onPress={() => {
                                    if (!expandAct && !expanded) {
                                        setExpanded(!expanded)
                                    }
                                    setExpandAct(!expandAct)
                                }}
                                name={expandAct ? "MdExpandMore" : "MdChevronRight"}
                                size={20}
                            />
                            <Text style={[s.spanS, { fontWeight: '600', width: 120 }]}>
                                Act. Weekly Cost
                            </Text>
                        </View>
                        {expanded && expandAct ? <View>
                            <Text style={[s.span, { paddingLeft: 24, width: 140 }]}>
                                Burn Rate
                            </Text>
                            <Text style={[s.span, { paddingLeft: 24, width: 140 }]}>
                                Hours/Week
                            </Text>
                            <Text style={[s.span, { paddingLeft: 24, width: 140 }]}>
                                Crew Size
                            </Text>
                        </View> : null}
                        <Text style={[s.spanS, { backgroundColor: '#f3f4f6', fontWeight: '600', width: 140 }]}>
                            Est. Hours
                        </Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Icon3
                                style={{ cursor: 'pointer' }}
                                onPress={() => {
                                    setExpandClassificationHours(!expandClassificationHours)
                                }}
                                name={expandClassificationHours ? "MdExpandMore" : "MdChevronRight"}
                                size={20}
                            />
                            <Text style={[s.spanS, { fontWeight: '600', width: 120 }]}>
                                Act. Hours
                            </Text>
                        </View>
                        {expandClassificationHours && classificationNames.length > 0 ? <View>
                            {classificationNames.map((cls, idx) => (
                                <View key={idx}>
                                    <View style={{ flexDirection: 'row' }}>
                                        <Icon3
                                            style={{ cursor: 'pointer' }}
                                            onPress={() => {
                                                setExpandedClassifications(prev => ({ ...prev, [cls]: !prev[cls] }))
                                            }}
                                            name={expandedClassifications[cls] ? "MdExpandMore" : "MdChevronRight"}
                                            size={16}
                                        />
                                        <Text style={[s.span, { fontSize: 9, width: 134, fontWeight: '600', color: '#1e40af', backgroundColor: '#f0f4ff' }]}>
                                            {cls}
                                        </Text>
                                    </View>
                                    {expandedClassifications[cls] && classificationPeopleNames[cls]?.length > 0 ? 
                                        classificationPeopleNames[cls].map((item, pIdx) => (
                                            <Text key={pIdx} style={[s.span, { fontSize: 8, width: 150, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', paddingLeft: 16 }]} title={`${item.name}${item.title ? ' - ' + item.title : ''}` }>
                                                {`${item.name}${item.title ? ' - ' + item.title : ''}` }
                                            </Text>
                                        ))
                                    : null}
                                </View>
                            ))}
                        </View> : null}
                        <Text style={[s.spanS, { backgroundColor: '#f3f4f6', fontWeight: '600', width: 140 }]}>
                            Planned
                        </Text>
                        {guardAdmin && isLiveVersion && <Text style={[s.spanS, { width: 140 }]}>
                            HEET
                        </Text>}
                    </View>
                    <View style={{ flexDirection: 'column', gap: 2, zIndex: 12 }}>
                        <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 'all')}><Text style={expandBtnSmallText(canGrowLeft)}>{`<`}</Text></Pressable>
                        <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 5)}><Text style={expandBtnSmallText(canGrowLeft)}>+5</Text></Pressable>
                        <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 2)}><Text style={expandBtnSmallText(canGrowLeft)}>+2</Text></Pressable>
                        <Pressable style={expandBtnSmall(canShrinkLeft)} onPress={() => canShrinkLeft && adjustWeekRange('left', -2)}><Text style={expandBtnSmallText(canShrinkLeft)}>-2</Text></Pressable>
                        <Pressable style={expandBtnSmall(canShrinkLeft)} onPress={() => canShrinkLeft && adjustWeekRange('left', -5)}><Text style={expandBtnSmallText(canShrinkLeft)}>-5</Text></Pressable>
                    </View>
                </View>
                {(expandedWeeks ? projectWeeks : visibleWeeks).map((week, index) => (
                    <View key={index}>
                        <WeekValue week={week} data={dataRows[rowId + '=' + week]} />
                    </View>
                ))}
                <View style={{ flexDirection: 'column', gap: 2, position: 'sticky', right: 0, zIndex: 12 }}>
                    <Pressable style={expandBtnSmall(canShrinkRight)} onPress={() => canShrinkRight && adjustWeekRange('right', -2)}><Text style={expandBtnSmallText(canShrinkRight)}>-2</Text></Pressable>
                    <Pressable style={expandBtnSmall(canShrinkRight)} onPress={() => canShrinkRight && adjustWeekRange('right', -5)}><Text style={expandBtnSmallText(canShrinkRight)}>-5</Text></Pressable>
                    <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 2)}><Text style={expandBtnSmallText(canGrowRight)}>+2</Text></Pressable>
                    <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 5)}><Text style={expandBtnSmallText(canGrowRight)}>+5</Text></Pressable>
                    <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 'all')}><Text style={expandBtnSmallText(canGrowRight)}>{`>`}</Text></Pressable>
                </View>
            </View>
        )
    }

    const forecastWeek = moment(versionInfo.Version_Start_DateTime).add(-7, 'days')

    // Phases sorted by code number, those with data first
    const prioritizedPhases = [...(phaseDescriptions || [])].sort((a, b) => {
        const aHasData = (pNum(dayZeroPhaseTotals[a.code]) + pNum(coPhaseTotals[a.code]) + pNum(dayZeroPhaseHourTotals[a.code]) + pNum(coPhaseHourTotals[a.code]) + pNum(estPhaseHourTotals[a.code]) + pNum(estPhaseTotals[a.code]) + pNum(actualPhaseHourTotals[a.code]) + pNum(actualPhaseTotals[a.code])) > 0 ? 1 : 0
        const bHasData = (pNum(dayZeroPhaseTotals[b.code]) + pNum(coPhaseTotals[b.code]) + pNum(dayZeroPhaseHourTotals[b.code]) + pNum(coPhaseHourTotals[b.code]) + pNum(estPhaseHourTotals[b.code]) + pNum(estPhaseTotals[b.code]) + pNum(actualPhaseHourTotals[b.code]) + pNum(actualPhaseTotals[b.code])) > 0 ? 1 : 0
        if (bHasData !== aHasData) return bHasData - aHasData;
        return Number(a.code) - Number(b.code);
    })

    // Always show all phases
    const visiblePhases = prioritizedPhases

    // Compute visible weeks based on range offset
    const visibleWeeks = React.useMemo(() => {
        if (!projectWeeks.length || !versionInfo?.Version_Start_DateTime) return projectWeeksCloseToToday;
        const todayWeek = moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek');
        const lastProjectWeek = moment(projectWeeks[projectWeeks.length - 1]);
        // If the job ended in the past, anchor to the last week with data
        const jobFinished = lastProjectWeek.isBefore(todayWeek, 'week');
        if (jobFinished) {
            // Find the last week that had actual data
            let lastDataIdx = projectWeeks.length - 1;
            for (let i = projectWeeks.length - 1; i >= 0; i--) {
                const wd = projectWeeksData[projectWeeks[i]];
                if (wd && (pNum(wd.actualDollars) > 0 || pNum(wd.etcDollars) > 0)) {
                    lastDataIdx = i;
                    break;
                }
            }
            const rangeSize = 15 + weekRangeLeft + weekRangeRight;
            const startIdx = Math.max(0, lastDataIdx - rangeSize + 1);
            return projectWeeks.slice(startIdx, lastDataIdx + 1);
        }
        const todayIdx = projectWeeks.findIndex(w => moment(w).isSameOrAfter(todayWeek, 'week'));
        if (todayIdx === -1) return projectWeeksCloseToToday;
        const pastCount = 5 + weekRangeLeft;
        const futureCount = 10 + weekRangeRight;
        const startIdx = Math.max(0, todayIdx - pastCount);
        const endIdx = Math.min(projectWeeks.length - 1, todayIdx + futureCount);
        return projectWeeks.slice(startIdx, endIdx + 1);
    }, [projectWeeks, projectWeeksCloseToToday, weekRangeLeft, weekRangeRight, versionInfo, projectWeeksData]);

    const canShrinkLeft = weekRangeLeft > 0 || expandedWeeks;
    const canShrinkRight = weekRangeRight > 0 || expandedWeeks;
    const canGrowLeft = expandedWeeks ? false : (() => { if (!projectWeeks.length || !versionInfo?.Version_Start_DateTime) return false; const todayIdx = projectWeeks.findIndex(w => moment(w).isSameOrAfter(moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek'), 'week')); return todayIdx > 0 && (todayIdx - (5 + weekRangeLeft)) > 0; })();
    const canGrowRight = expandedWeeks ? false : (() => { if (!projectWeeks.length || !versionInfo?.Version_Start_DateTime) return false; const todayIdx = projectWeeks.findIndex(w => moment(w).isSameOrAfter(moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek'), 'week')); return todayIdx >= 0 && (todayIdx + 10 + weekRangeRight) < projectWeeks.length - 1; })();

    // Weeks past the right edge of the window, so the header can say the job runs further out
    const hiddenWeeksRight = expandedWeeks || !visibleWeeks.length ? 0 : Math.max(0, projectWeeks.length - 1 - projectWeeks.indexOf(visibleWeeks[visibleWeeks.length - 1]))
    const hiddenWeeksRightTitle = hiddenWeeksRight > 0 ? `${hiddenWeeksRight} more weeks through ${moment(projectWeeks[projectWeeks.length - 1]).format('MMM D, YYYY')} — click > to show them all` : ''

    // Hours graph data
    const graphWeeks = expandedWeeks ? projectWeeks : visibleWeeks
    const graphMaxHours = Math.max(1, ...graphWeeks.map(w => Math.max(pNum(projectWeeksData[w]?.etcHours), pNum(projectWeeksData[w]?.actualHours))))

    const adjustWeekRange = (side, delta) => {
        if (expandedWeeks) {
            setExpandedWeeks(false);
            setWeekRangeLeft(0);
            setWeekRangeRight(0);
            return;
        }
        if (side === 'left') {
            if (delta === 'all') {
                const todayIdx = projectWeeks.findIndex(w => moment(w).isSameOrAfter(moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek'), 'week'));
                setWeekRangeLeft(Math.max(0, todayIdx - 5));
            } else {
                setWeekRangeLeft(prev => Math.max(0, prev + delta));
            }
        } else {
            if (delta === 'all') {
                const todayIdx = projectWeeks.findIndex(w => moment(w).isSameOrAfter(moment(versionInfo.Version_Start_DateTime).add(-7, 'days').startOf('isoWeek'), 'week'));
                setWeekRangeRight(Math.max(0, projectWeeks.length - 1 - todayIdx - 10));
            } else {
                setWeekRangeRight(prev => Math.max(0, prev + delta));
            }
        }
    };

    const expandButtonStyle = { ...expandButton, zIndex: 12 };
    const expandBtnSmall = (active) => ({ padding: '2px 4px', backgroundColor: active ? '#f1f5f9' : '#f8f9fa', borderRadius: 4, border: active ? '1px solid #e2e8f0' : '1px solid #f1f3f5', cursor: active ? 'pointer' : 'default', zIndex: 12, opacity: active ? 1 : 0.35 });
    const expandBtnSmallText = (active) => ({ fontSize: 10, fontWeight: '600', color: active ? '#475569' : '#94a3b8' });

    const togglePhaseCollapse = (code) => {
        setCollapsedPhases(prev => ({ ...prev, [code]: !prev[code] }))
    }

    const toggleCollapseAll = () => {
        const allCollapsed = prioritizedPhases.every(p => collapsedPhases[p.code])
        const newState = {}
        prioritizedPhases.forEach(p => { newState[p.code] = !allCollapsed })
        setCollapsedPhases(newState)
    }

    const handleTabPress = (code) => {
        setSelectedPhaseTab(selectedPhaseTab === code ? 'all' : code)
        // Expand the phase
        setCollapsedPhases(prev => ({ ...prev, [code]: false }))
        // Scroll to it after a tick
        setTimeout(() => {
            phaseRefs.current[code]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
    }

    // Default all phases to collapsed on first load
    if (collapsedPhases === 'init' && phaseDescriptions && phaseDescriptions.length > 0) {
        const defaults = {}
        phaseDescriptions.forEach(p => { defaults[p.code] = true })
        setCollapsedPhases(defaults)
    }

    const renderManpowerPanel = () => {
        if (!manpowerOpen) return null
        const anchor = versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime) : moment()
        const thisWeek = anchor.clone().startOf('isoWeek').format('YYYY-MM-DD')
        const nextWeek = moment(thisWeek).add(1, 'week').format('YYYY-MM-DD')
        const fmt = (w) => moment(w).format('MMM D')
        const rows = (phaseDescriptions || []).map(phase => {
            const w0 = userData?.[phase.code + '=' + thisWeek]
            const w1 = userData?.[phase.code + '=' + nextWeek]
            const c0 = w0?.Number_Units ? pNum(w0.Number_Units) : 0
            const c1 = w1?.Number_Units ? pNum(w1.Number_Units) : 0
            return { code: phase.code, description: phase.description, c0, c1, total: c0 + c1 }
        }).filter(r => r.total > 0).sort((a, b) => b.total - a.total)
        const round2 = (n) => Math.round(n * 100) / 100
        const tot0 = round2(rows.reduce((s, r) => s + r.c0, 0))
        const tot1 = round2(rows.reduce((s, r) => s + r.c1, 0))
        return (
            <View style={{ position: 'fixed', right: 16, top: 100, width: 240, maxHeight: '70vh', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100, overflow: 'hidden' }}>
                <View style={{ padding: 8, paddingHorizontal: 10, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text style={{ fontSize: 12, fontWeight: '600', color: '#334155' }}>Manpower — next 2 weeks</Text>
                    <Pressable onPress={() => setManpowerOpen(false)} style={{ padding: 2, paddingHorizontal: 6, cursor: 'pointer' }}>
                        <Text style={{ fontSize: 14, color: '#64748b' }}>×</Text>
                    </Pressable>
                </View>
                <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', padding: 4, paddingHorizontal: 8 }}>
                    <View style={{ flex: 1 }}><Text style={{ fontSize: 10, color: '#64748b', fontWeight: '600' }}>Phase</Text></View>
                    <View style={{ width: 44, alignItems: 'flex-end' }}><Text style={{ fontSize: 10, color: '#64748b', fontWeight: '600' }}>{fmt(thisWeek)}</Text></View>
                    <View style={{ width: 44, alignItems: 'flex-end' }}><Text style={{ fontSize: 10, color: '#64748b', fontWeight: '600' }}>{fmt(nextWeek)}</Text></View>
                </View>
                <View style={{ maxHeight: 'calc(70vh - 96px)', overflow: 'auto' }}>
                    {rows.length === 0 ? (
                        <View style={{ padding: 12 }}><Text style={{ fontSize: 11, color: '#94a3b8' }}>No staffed phases in this window.</Text></View>
                    ) : rows.map((r, i) => (
                        <View key={r.code} style={{ flexDirection: 'row', padding: 4, paddingHorizontal: 8, borderBottom: '1px solid #f1f5f9', backgroundColor: i % 2 ? '#fafbfc' : 'white', alignItems: 'center' }}>
                            <View style={{ flex: 1, flexDirection: 'row', gap: 4, overflow: 'hidden' }}>
                                <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: '600', width: 32 }}>{r.code}</Text>
                                <Text style={{ fontSize: 10, color: '#334155', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.description || ''}</Text>
                            </View>
                            <View style={{ width: 44, alignItems: 'flex-end' }}><Text style={{ fontSize: 11, color: '#334155' }}>{r.c0 || ''}</Text></View>
                            <View style={{ width: 44, alignItems: 'flex-end' }}><Text style={{ fontSize: 11, color: '#334155' }}>{r.c1 || ''}</Text></View>
                        </View>
                    ))}
                </View>
                <View style={{ flexDirection: 'row', padding: 6, paddingHorizontal: 8, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', alignItems: 'center' }}>
                    <View style={{ flex: 1 }}><Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '700' }}>Total crew</Text></View>
                    <View style={{ width: 44, alignItems: 'flex-end' }}><Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '700' }}>{tot0}</Text></View>
                    <View style={{ width: 44, alignItems: 'flex-end' }}><Text style={{ fontSize: 11, color: '#1e40af', fontWeight: '700' }}>{tot1}</Text></View>
                </View>
            </View>
        )
    }

    return (
        <View style={{ padding: 20, marginBottom: 100 }}>
                {renderManpowerPanel()}
                {/* Subtab switcher: Burn Tool ↔ Manpower Planning */}
                {canSeeWipTabs && (
                <View style={{ flexDirection: 'row', gap: 4, marginBottom: 14, borderBottom: `1px solid ${colors.borderLight}`, paddingBottom: 0 }}>
                    {[{ key: 'burn', label: 'Phased Labor' }, { key: 'manpower', label: 'Manpower Planning' }].filter((t) => !isPC || t.key === 'manpower').map((t) => (
                        <Pressable
                            key={t.key}
                            onPress={() => setMpSubTab(t.key)}
                            style={{
                                paddingTop: 8,
                                paddingBottom: 8,
                                paddingLeft: 16,
                                paddingRight: 16,
                                borderBottom: `2px solid ${activeMpTab === t.key ? colors.accent : 'transparent'}`,
                                cursor: 'pointer',
                            }}
                        >
                            <Text style={{ fontSize: 14, fontWeight: activeMpTab === t.key ? '700' : '500', color: activeMpTab === t.key ? colors.accent : colors.textSecondary }}>{t.label}</Text>
                        </Pressable>
                    ))}
                </View>
                )}
                {activeMpTab === 'manpower' && (
                    <ManpowerPlanning
                        expandedWeeks={expandedWeeks}
                        projectWeeks={projectWeeks}
                        visibleWeeks={visibleWeeks}
                        versionInfo={versionInfo}
                        currentBudgetHours={currentBudgetHours}
                        actualHours={actualHours}
                        etcHours={etcHours}
                        totalHours={totalHours}
                        overUnderHours={overUnderHours}
                        dayZeroHourTotal={dayZeroHourTotal}
                        coHours={coHours}
                        phaseDescriptions={phaseDescriptions}
                        userData={userData}
                        estPhaseHourTotals={estPhaseHourTotals}
                        actualPhaseHourTotals={actualPhaseHourTotals}
                        jobCostDetail={jobCostDetail}
                        projectDates={projectDates}
                        projectWeeksData={projectWeeksData}
                        forecastWeek={forecastWeek}
                    />
                )}
                {activeMpTab === 'burn' && <>
                <View style={{ marginBottom: 10, alignItems: 'flex-end', flexDirection: 'row', justifyContent: 'flex-end', gap: 10 }}>
                    <Pressable
                        onPress={() => setManpowerOpen(v => !v)}
                        style={{
                            alignItems: 'center',
                            backgroundColor: manpowerOpen ? '#dbeafe' : '#f8fafc',
                            padding: 12,
                            paddingHorizontal: 16,
                            borderRadius: 6,
                            flexDirection: 'row',
                            border: `1px solid ${manpowerOpen ? '#1e40af' : '#cbd5e1'}`,
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        }}
                    >
                        <Text style={{ color: manpowerOpen ? '#1e40af' : '#334155', fontWeight: '500', fontSize: 14 }}>Manpower</Text>
                    </Pressable>
                    {guardAdmin && <Pressable
                        onPress={exportHEETToExcel}
                        style={{ 
                            alignItems: 'center',
                            backgroundColor: (!jobHEETData || jobHEETData.length === 0) ? '#e2e8f0' : '#f8fafc',
                            padding: 12,
                            paddingHorizontal: 16,
                            borderRadius: 6,
                            flexDirection: 'row',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                            opacity: (!jobHEETData || jobHEETData.length === 0) ? 0.5 : 1,
                            cursor: (!jobHEETData || jobHEETData.length === 0) ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <Text style={{ marginRight: 10, color: '#334155', fontWeight: '500', fontSize: 14 }}>Export HEET</Text>
                        <Icon name="↓" size={18} color="#334155" />
                    </Pressable>}
                    <Pressable 
                        onPress={isExportingTemplate ? null : exportToExcelTemplate}
                        style={{ 
                            alignItems: 'center',
                            backgroundColor: isExportingTemplate ? '#e2e8f0' : '#f8fafc',
                            padding: 12,
                            paddingHorizontal: 16,
                            borderRadius: 6,
                            flexDirection: 'row',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                            opacity: isExportingTemplate ? 0.6 : 1,
                            cursor: isExportingTemplate ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <Text style={{ marginRight: 10, color: '#334155', fontWeight: '500', fontSize: 14 }}>
                            {isExportingTemplate ? 'Exporting...' : 'Export Template'}
                        </Text>
                        {!isExportingTemplate && <Icon name="↓" size={18} color="#334155" />}
                    </Pressable>
                    <Pressable 
                        onPress={exportToExcel}
                        style={{ 
                            alignItems: 'center',
                            backgroundColor: '#f8fafc',
                            padding: 12,
                            paddingHorizontal: 16,
                            borderRadius: 6,
                            flexDirection: 'row',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <Text style={{ marginRight: 10, color: '#334155', fontWeight: '500', fontSize: 14 }}>Export to Excel</Text>
                        <Icon name="↓" size={18} color="#334155" />
                    </Pressable>
                    <Pressable
                        onPress={uploadPreview === 'reading' ? null : () => uploadInputRef.current && uploadInputRef.current.click()}
                        title="Upload an edited Export to Excel file. You review every change before it is applied."
                        style={{
                            alignItems: 'center',
                            backgroundColor: uploadPreview === 'reading' ? '#e2e8f0' : '#f8fafc',
                            padding: 12,
                            paddingHorizontal: 16,
                            borderRadius: 6,
                            flexDirection: 'row',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                            opacity: uploadPreview === 'reading' ? 0.6 : 1,
                            cursor: uploadPreview === 'reading' ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <Text style={{ marginRight: 10, color: '#334155', fontWeight: '500', fontSize: 14 }}>{uploadPreview === 'reading' ? 'Reading…' : 'Upload changes'}</Text>
                        {uploadPreview !== 'reading' && <Icon name="↑" size={18} color="#334155" />}
                    </Pressable>
                    <input type="file" ref={uploadInputRef} accept=".xlsx" style={{ display: 'none', height: 0, width: 0 }} onChange={handleUploadFile} />
                </View>
                <View style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <View ref={containerRef} style={stickyHeader}>
                        <View style={{ flexDirection: 'row', position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 11 }}>
                            <View>
                                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 2 }}>
                                    <Pressable
                                        onPress={toggleCollapseAll}
                                        style={{ padding: 2, cursor: 'pointer' }}
                                    >
                                        <Icon3
                                            name={prioritizedPhases.every(p => collapsedPhases[p.code]) ? 'MdUnfoldMore' : 'MdUnfoldLess'}
                                            size={16}
                                            color="#64748b"
                                        />
                                    </Pressable>
                                </View>
                                <View>
                                    <View style={{ flexDirection: 'row' }}>
                                        <View style={{ width: 40 }}>
                                            <Text style={[s.total, { width: 40, maxWidth: 40 }]}>Cost</Text>
                                            <Icon3
                                                style={{ cursor: 'pointer' }}
                                                onPress={() => {
                                                    setDollarsExpanded(!dollarsExpanded)
                                                }}
                                                name={dollarsExpanded ? "MdExpandMore" : "MdChevronRight"}
                                                size={20}
                                            />
                                        </View>
                                        <View>
                                            <Text style={s.total}>Budget</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{dayZeroDollars}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>COs</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{coDollars}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>Cur. Bdgt</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{currentBudgetDollars}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>Actuals</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{actualDollars}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>ETC</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{etcDollars}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>Total</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{totalDollars}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>Ov/Under</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6', color: pNum(overUnderDollars) > 0 ? '#dc2626' : undefined, fontWeight: pNum(overUnderDollars) > 0 ? '600' : undefined }]}>{overUnderDollars}</Text>
                                        </View>
                                        {/* <View>
                                            <Text style={[s.total, { color: '#1565c0' }]}>HEET</Text>
                                            <Text style={[s.total, { backgroundColor: '#e3f2fd', color: '#1565c0' }]}>{heetTotal ? fNum(heetTotal, 1) : ''}</Text>
                                        </View> */}
                                    </View>
                                </View>
                                {dollarsExpanded ? <View>
                                    {phaseDescriptions.map((phase, i) => {
                                        const budget = phase?.code ? coPhaseTotals[phase?.code] && dayZeroPhaseTotals[phase?.code] ? dayZeroPhaseTotals[phase?.code] + coPhaseTotals[phase?.code] : dayZeroPhaseTotals[phase?.code] ? dayZeroPhaseTotals[phase?.code] : coPhaseTotals[phase?.code] ? coPhaseTotals[phase?.code] : 0 : 0
                                        const total = estPhaseTotals[phase?.code] && actualPhaseTotals[phase?.code] ? actualPhaseTotals[phase?.code] + estPhaseTotals[phase?.code] : actualPhaseTotals[phase?.code] ? actualPhaseTotals[phase?.code] : estPhaseTotals[phase?.code] ? estPhaseTotals[phase?.code] : 0
                                        const overUnder = fNum(total - budget, 1)
                                        return (
                                            <View key={i} style={{ flexDirection: 'row' }}>
                                                <View style={[s.total, { padding: 0, height: 30, overflow: 'hidden', width: 40, maxWidth: 40 }]}>
                                                    <Text style={[s.totalS, { fontSize: 12 }]}>{phase?.code}</Text>
                                                    <Text style={s.totalS}>{phase?.description}</Text>
                                                </View>
                                                <View>
                                                    <Text style={s.total}>{phase?.code && dayZeroPhaseTotals[phase?.code] ? fNum(dayZeroPhaseTotals[phase?.code], 1) : ''}</Text>
                                                </View>
                                                <View>
                                                    <Text style={s.total}>{phase?.code && coPhaseTotals[phase?.code] ? fNum(coPhaseTotals[phase?.code], 1) : ''}</Text>
                                                </View>
                                                <View>
                                                    <Text style={s.total}>{phase?.code ? coPhaseTotals[phase?.code] && dayZeroPhaseTotals[phase?.code] ? fNum(dayZeroPhaseTotals[phase?.code] + coPhaseTotals[phase?.code], 1) : dayZeroPhaseTotals[phase?.code] ? fNum(dayZeroPhaseTotals[phase?.code], 1) : coPhaseTotals[phase?.code] ? fNum(coPhaseTotals[phase?.code], 1) : '' : ''}</Text>
                                                </View>
                                                <View>
                                                    <Text style={s.total}>{phase?.code && actualPhaseTotals[phase?.code] ? fNum(actualPhaseTotals[phase?.code], 1) : ''}</Text>
                                                </View>
                                                <View>
                                                    <Text style={s.total}>{phase?.code && estPhaseTotals[phase?.code] ? fNum(estPhaseTotals[phase?.code], 1) : ''}</Text>
                                                </View>
                                                <View>
                                                    <Text style={s.total}>{phase?.code ? estPhaseTotals[phase?.code] && actualPhaseTotals[phase?.code] ? fNum(actualPhaseTotals[phase?.code] + estPhaseTotals[phase?.code], 1) : actualPhaseTotals[phase?.code] ? fNum(actualPhaseTotals[phase?.code]) : estPhaseTotals[phase?.code] ? fNum(estPhaseTotals[phase?.code], 1) : '' : ''}</Text>
                                                </View>
                                                <View>
                                                    <Text style={[s.total, { color: pNum(overUnder) > 0 ? '#dc2626' : undefined, fontWeight: pNum(overUnder) > 0 ? '600' : undefined }]}>{overUnder}</Text>
                                                </View>
                                                {guardAdmin && isLiveVersion && <View>
                                                    <Text style={s.total}>{phase?.code && heetPhaseTotals[phase?.code] ? fNum(heetPhaseTotals[phase?.code], 1) : ''}</Text>
                                                </View>}
                                            </View>
                                        )
                                    })}
                                </View> : null}
                                <View>
                                    <View style={{ flexDirection: 'row' }}>
                                        <View style={{ width: 40 }}>
                                            <Text style={[s.total, { width: 40, maxWidth: 40 }]}>Hours</Text>
                                            <Icon3
                                                style={{ cursor: 'pointer' }}
                                                onPress={() => {
                                                    setHoursExpanded(!hoursExpanded)
                                                }}
                                                name={hoursExpanded ? "MdExpandMore" : "MdChevronRight"}
                                                size={20}
                                            />
                                        </View>
                                        <View>
                                            <Text style={s.total}>Budget</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{dayZeroHourTotal}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>COs</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{coHours}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>Cur. Bdgt</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{currentBudgetHours}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>Actuals</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{actualHours}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>ETC</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{etcHours}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>Total</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6' }]}>{totalHours}</Text>
                                        </View>
                                        <View>
                                            <Text style={s.total}>Ov/Under</Text>
                                            <Text style={[s.total, { backgroundColor: '#f3f4f6', color: pNum(overUnderHours) > 0 ? '#dc2626' : undefined, fontWeight: pNum(overUnderHours) > 0 ? '600' : undefined }]}>{overUnderHours}</Text>
                                        </View>
                                    </View>
                                </View>
                                {hoursExpanded ? <View>
                                    {phaseDescriptions.map((phase, i) => (
                                        <View key={i} style={{ flexDirection: 'row' }}>
                                            <View style={[s.total, { padding: 0, height: 30, overflow: 'hidden', width: 40, maxWidth: 40 }]}>
                                                <Text style={[s.totalS, { fontSize: 12 }]}>{phase?.code}</Text>
                                                <Text style={s.totalS}>{phase?.description}</Text>
                                            </View>
                                            <View>
                                                <Text style={s.total}>{phase?.code && dayZeroPhaseHourTotals[phase?.code] ? fNum(dayZeroPhaseHourTotals[phase?.code], 1) : ''}</Text>
                                            </View>
                                            <View>
                                                <Text style={s.total}>{phase?.code && coPhaseHourTotals[phase?.code] ? fNum(coPhaseHourTotals[phase?.code], 1) : ''}</Text>
                                            </View>
                                            <View>
                                                <Text style={s.total}>{phase?.code ? coPhaseHourTotals[phase?.code] && dayZeroPhaseHourTotals[phase?.code] ? fNum(dayZeroPhaseHourTotals[phase?.code] + coPhaseHourTotals[phase?.code], 1) : dayZeroPhaseHourTotals[phase?.code] ? fNum(dayZeroPhaseHourTotals[phase?.code], 1) : coPhaseHourTotals[phase?.code] ? fNum(coPhaseHourTotals[phase?.code], 1) : '' : ''}</Text>
                                            </View>
                                            <View>
                                                <Text style={s.total}>{phase?.code && actualPhaseHourTotals[phase?.code] ? fNum(actualPhaseHourTotals[phase?.code], 1) : ''}</Text>
                                            </View>
                                            <View>
                                                <Text style={s.total}>{phase?.code && estPhaseHourTotals[phase?.code] ? fNum(estPhaseHourTotals[phase?.code], 1) : ''}</Text>
                                            </View>
                                            <View>
                                                <Text style={s.total}>{phase?.code ? estPhaseHourTotals[phase?.code] && actualPhaseHourTotals[phase?.code] ? fNum(actualPhaseHourTotals[phase?.code] + estPhaseHourTotals[phase?.code], 1) : actualPhaseHourTotals[phase?.code] ? fNum(actualPhaseHourTotals[phase?.code], 1) : estPhaseHourTotals[phase?.code] ? fNum(estPhaseHourTotals[phase?.code], 1) : '' : ''}</Text>
                                            </View>
                                            <View>
                                                <Text style={s.total}></Text>
                                            </View>
                                        </View>
                                    ))}
                                </View> : null}
                                {/* <Pressable
                                    style={{ alignItems: 'center', backgroundColor: '#f3f4f6', padding: 10, borderRadius: 5, flexDirection: 'row' }}
                                    onPress={() => {
                                        setExpandedWeeks(!expandedWeeks)
                                    }}
                                >
                                    <Text style={{ marginRight: 10 }}>Expand Weeks</Text>
                                </Pressable> */}
                            </View>
                            <View>
                                <Text style={s.spanSC}>
                                    <Pressable
                                        onPress={() => {
                                            reloadUserData()
                                        }}>
                                <Icon3 name="MdCalculate" size={15} color="#64748b" style={{ marginRight: 8, cursor: 'pointer' }} />
                                    </Pressable>
                                </Text>
                                {!showGraphView && <>
                                {topSectionExpanded && <>
                                <Text style={s.spanTop}>
                                    Burn Rate Est
                                </Text>
                                <Text style={s.spanTop}>
                                    Burn Rate Act
                                </Text>
                                <Text style={s.spanTop}>
                                    Avg Hrs/Wk
                                </Text>
                                <Text style={s.spanTop}>
                                    Total Crew Est
                                </Text>
                                <Text style={s.spanTop}>
                                    Total Crew Act
                                </Text>
                                <Text style={s.spanTop}>
                                    Total Hrs Est
                                </Text>
                                <Text style={s.spanTop}>
                                    Total Hrs Act
                                </Text>
                                </>}
                                <Text style={s.spanTop}>
                                    Total $ Est
                                </Text>
                                <Text style={s.spanTop}>
                                    Total $ Act
                                </Text>
                                {guardAdmin && isLiveVersion && <Text style={s.spanTop}>
                                    HEET
                                </Text>}
                                </>}
                                {showGraphView && <View style={{ height: 234, justifyContent: 'flex-end', alignItems: 'center', paddingBottom: 4 }}>
                                    <View style={{ flexDirection: 'row', gap: 8, marginBottom: 6 }}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                                            <View style={{ width: 8, height: 4, backgroundColor: '#93c5fd', borderRadius: 1 }} />
                                            <Text style={{ fontSize: 9, color: '#64748b' }}>Est</Text>
                                        </View>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                                            <View style={{ width: 8, height: 4, backgroundColor: '#1e40af', borderRadius: 1 }} />
                                            <Text style={{ fontSize: 9, color: '#64748b' }}>Act</Text>
                                        </View>
                                    </View>
                                </View>}
                                <View style={{ flexDirection: 'row', gap: 4, marginTop: 4 }}>
                                <Pressable
                                    onPress={() => setTopSectionExpanded(!topSectionExpanded)}
                                    style={{ padding: 4, backgroundColor: topSectionExpanded ? '#1e40af' : '#f1f5f9', borderRadius: 4, alignItems: 'center' }}
                                >
                                    <Icon3 name={topSectionExpanded ? "MdExpandLess" : "MdExpandMore"} size={16} color={topSectionExpanded ? 'white' : '#64748b'} />
                                </Pressable>
                                <Pressable
                                    onPress={() => setShowGraphView(!showGraphView)}
                                    style={{ padding: 4, backgroundColor: showGraphView ? '#1e40af' : '#f1f5f9', borderRadius: 4, alignItems: 'center' }}
                                >
                                    <Icon3 name={showGraphView ? "MdBarChart" : "MdBarChart"} size={16} color={showGraphView ? 'white' : '#64748b'} />
                                </Pressable>
                                </View>
                                {/* {phases.map((row, index) => (
                        <View>
                            <Text style={s.span}>
                            </Text>
                            <Text style={s.span}>
                                Est. Weekly Cost
                            </Text>
                            <Text style={s.span}>
                                Act. Weekly Cost
                            </Text>
                            <Text style={s.span}>
                                Est. Hours
                            </Text>
                            <Text style={s.span}>
                                Act. Hours
                            </Text>
                        </View>
                        ))} */}
                            </View>
                            <View style={{ flexDirection: 'column', gap: 2, zIndex: 12 }}>
                                <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 'all')}><Text style={expandBtnSmallText(canGrowLeft)}>{`<`}</Text></Pressable>
                                <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 5)}><Text style={expandBtnSmallText(canGrowLeft)}>+5</Text></Pressable>
                                <Pressable style={expandBtnSmall(canGrowLeft)} onPress={() => canGrowLeft && adjustWeekRange('left', 2)}><Text style={expandBtnSmallText(canGrowLeft)}>+2</Text></Pressable>
                                <Pressable style={expandBtnSmall(canShrinkLeft)} onPress={() => canShrinkLeft && adjustWeekRange('left', -2)}><Text style={expandBtnSmallText(canShrinkLeft)}>-2</Text></Pressable>
                                <Pressable style={expandBtnSmall(canShrinkLeft)} onPress={() => canShrinkLeft && adjustWeekRange('left', -5)}><Text style={expandBtnSmallText(canShrinkLeft)}>-5</Text></Pressable>
                            </View>
                        </View>
                        {/* projectData[week] = {
                        actualDollars: actualDollarsWeek,
                        actualHours: actualHoursWeek,
                        actualCrewSize: actualCrewSize,
                        etcDollars: etcDollarsWeek,
                        etcHours: etcHoursWeek

                    } */}
                        {showGraphView ? (expandedWeeks ? projectWeeks : visibleWeeks).map((row, index) => {
                            const estH = pNum(projectWeeksData[row]?.etcHours)
                            const actH = pNum(projectWeeksData[row]?.actualHours)
                            const estBarHeight = graphMaxHours > 0 ? (estH / graphMaxHours) * 220 : 0
                            const actBarHeight = graphMaxHours > 0 ? (actH / graphMaxHours) * 220 : 0
                            const isForecastWeek = forecastWeek.isSame(moment(row), 'week')
                            return (
                                <View key={index} style={{ width: 60, borderRight: isForecastWeek ? '2px solid #1e40af' : 'none' }}>
                                    <Text style={[s.rateC, weekDateHeader]}>{row}</Text>
                                    <View style={{ height: 234, flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'center', gap: 1, paddingBottom: 2, paddingHorizontal: 4 }}>
                                        {estH > 0 && <View style={{ width: 20, height: Math.max(estBarHeight, 3), backgroundColor: '#93c5fd', borderTopLeftRadius: 2, borderTopRightRadius: 2 }} />}
                                        {actH > 0 && <View style={{ width: 20, height: Math.max(actBarHeight, 3), backgroundColor: '#1e40af', borderTopLeftRadius: 2, borderTopRightRadius: 2 }} />}
                                    </View>
                                </View>
                            )
                        }) : expandedWeeks ? projectWeeks.map((row, index) => (
                            <View key={index} style={{ borderRight: forecastWeek.isSame(moment(row), 'week') ? '2px solid #1e40af' : 'none' }}>
                                <Text style={[s.rateC, weekDateHeader]}>{row}</Text>
                                {topSectionExpanded && <>
                                <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.estBurnRate}</Text>
                                <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.actualBurnRate}</Text>
                                <Text style={s.rate}>{projectWeeksData[row]?.avgHoursWeek}</Text>
                                <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.etcCrewSize}</Text>
                                <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.actualCrewSize}</Text>
                                <Text style={s.rate}>{projectWeeksData[row]?.etcHours}</Text>
                                <Text style={s.rate}>{projectWeeksData[row]?.actualHours}</Text>
                                </>}
                                <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.etcDollars}</Text>
                                <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.actualDollars}</Text>
                                {guardAdmin && isLiveVersion && <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{heetWeeklyData[row] ? fNum(heetWeeklyData[row], 1) : ''}</Text>}
                            </View>)) : visibleWeeks.map((row, index) => (
                                <View key={index} style={{ borderRight: forecastWeek.isSame(moment(row), 'week') ? '2px solid #1e40af' : 'none' }}>
                                    <Text style={[s.rateC, weekDateHeader]}>{row}</Text>
                                    {topSectionExpanded && <>
                                    <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.estBurnRate}</Text>
                                    <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.actualBurnRate}</Text>
                                    <Text style={s.rate}>{projectWeeksData[row]?.avgHoursWeek}</Text>
                                    <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.etcCrewSize}</Text>
                                    <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.actualCrewSize}</Text>
                                    <Text style={s.rate}>{projectWeeksData[row]?.etcHours}</Text>
                                    <Text style={s.rate}>{projectWeeksData[row]?.actualHours}</Text>
                                    </>}
                                    <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.etcDollars}</Text>
                                    <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{projectWeeksData[row]?.actualDollars}</Text>
                                    {guardAdmin && isLiveVersion && <Text style={[s.rate, { backgroundColor: '#f3f4f6' }]}>{heetWeeklyData[row] ? fNum(heetWeeklyData[row], 1) : ''}</Text>}
                                </View>))}
                        <View style={{ flexDirection: 'column', gap: 2, position: 'sticky', right: 0, zIndex: 12 }}>
                            <Pressable style={expandBtnSmall(canShrinkRight)} onPress={() => canShrinkRight && adjustWeekRange('right', -2)}><Text style={expandBtnSmallText(canShrinkRight)}>-2</Text></Pressable>
                            <Pressable style={expandBtnSmall(canShrinkRight)} onPress={() => canShrinkRight && adjustWeekRange('right', -5)}><Text style={expandBtnSmallText(canShrinkRight)}>-5</Text></Pressable>
                            <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 2)}><Text style={expandBtnSmallText(canGrowRight)}>+2</Text></Pressable>
                            <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 5)}><Text style={expandBtnSmallText(canGrowRight)}>+5</Text></Pressable>
                            <Pressable style={expandBtnSmall(canGrowRight)} onPress={() => canGrowRight && adjustWeekRange('right', 'all')} title={hiddenWeeksRight > 0 ? hiddenWeeksRightTitle : undefined}><Text style={expandBtnSmallText(canGrowRight)}>{`>`}</Text></Pressable>
                            {hiddenWeeksRight > 0 && <Text style={{ fontSize: 9, fontWeight: '600', color: '#1e40af', textAlign: 'center', lineHeight: 11 }} title={hiddenWeeksRightTitle}>{`${hiddenWeeksRight} more\nweeks`}</Text>}
                        </View>
                    </View>
                    <View style={sectionDivider}></View>
                </View>
                {visiblePhases.map((phase, i) => {
                    const phaseHasData = pNum(dayZeroPhaseTotals[phase.code]) + pNum(coPhaseTotals[phase.code]) + pNum(estPhaseHourTotals[phase.code]) + pNum(actualPhaseHourTotals[phase.code]) > 0
                    const phaseBudget = pNum(dayZeroPhaseHourTotals[phase.code]) + pNum(coPhaseHourTotals[phase.code])
                    const phaseTotalCost = pNum(actualPhaseTotals[phase.code]) + pNum(estPhaseTotals[phase.code])
                    const isCollapsed = collapsedPhases[phase.code]
                    return (
                    <View ref={el => phaseRefs.current[phase.code] = el} style={{ zIndex: 0, marginBottom: isCollapsed ? 2 : 0 }} key={phase.code || i}>
                        {/* A collapsed phase: one 32 px row, every segment a fixed width on a single
                            line (nothing wraps, so every row is the same height); the week cells to
                            its right share that height. */}
                        {isCollapsed && <View style={{ flexDirection: 'row', alignItems: 'stretch', marginTop: 4, height: 32 }}>
                            <Pressable
                                onPress={() => togglePhaseCollapse(phase.code)}
                                style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    height: 32,
                                    paddingVertical: 0,
                                    paddingHorizontal: 0,
                                    backgroundColor: '#f8fafc',
                                    borderRadius: 6,
                                    border: '1px solid #e2e8f0',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 5,
                                    width: 592, // the header's sticky block is 575 wide at x 97; this row starts at x 80
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <View style={{ flexDirection: 'row', alignItems: 'center', width: 22, justifyContent: 'center', flexShrink: 0 }}>
                                    <Icon3
                                        name="MdChevronRight"
                                        size={16}
                                        color="#475569"
                                    />
                                </View>
                                <Text style={{ fontSize: 12, fontWeight: '700', color: '#1e293b', width: 56, flexShrink: 0, whiteSpace: 'nowrap' }}>{phase.code}</Text>
                                <Text numberOfLines={1} title={phase.description} style={{ fontSize: 11, color: '#64748b', width: 140, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{phase.description}</Text>
                                {userData[phase.code]?.Phase_Start && userData[phase.code]?.Phase_End ? (
                                    <Text style={{ fontSize: 10, color: '#6b7280', width: 108, flexShrink: 0, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{moment(userData[phase.code].Phase_Start).add(6, 'hours').format('M/D/YY')} - {moment(userData[phase.code].Phase_End).add(6, 'hours').format('M/D/YY')}</Text>
                                ) : <Text style={{ width: 108, flexShrink: 0 }}></Text>}

                {(() => {
                    const hji = heavyJobInstalledByPhase[phase.code] || 0
                    const inst = scorecardData[phase.code]?.installed ? pNum(scorecardData[phase.code].installed) : 0
                    const totalInst = (isNaN(pNum(hji)) ? 0 : pNum(hji)) + (isNaN(inst) ? 0 : inst)
                    const dzQty = pNum(dayZeroPhaseQuantities[phase.code]) || 0
                    const coQty = pNum(coPhaseQuantities[phase.code]) || 0
                    const plug = scorecardData[phase.code]?.plug ? pNum(scorecardData[phase.code].plug) : 0
                    const totalQty = dzQty + coQty + (isNaN(plug) ? 0 : plug)
                    const pct = totalQty > 0 ? (100 * totalInst / totalQty) : 0
                    const remainingHrs = pNum(estPhaseHourTotals[phase.code]) || 0
                    const isOverWithRemaining = pct >= 100 && remainingHrs > 0
                    const etcRate = remainingHrs > 0 ? pNum(estPhaseTotals[phase.code]) / remainingHrs : 0
                    const actualHrs = pNum(actualPhaseHourTotals[phase.code]) || 0
                    const actRate = actualHrs > 0 ? pNum(actualPhaseTotals[phase.code]) / actualHrs : 0
                    // Each figure sits in a fixed-width slot, numbers right-aligned so they line
                    // up down the column; the unit is a quiet suffix.
                    const slot = (w, extra) => ({ width: w, flexShrink: 0, whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums', paddingRight: 10, ...extra })
                    const unit = { fontSize: 9, fontWeight: '500', color: '#94a3b8' }
                    return <>
                        {remainingHrs > 0
                            ? <Text style={slot(78, { fontSize: 11, color: isOverWithRemaining ? '#dc2626' : '#1e40af', fontWeight: '600' })} title="Estimate to complete, hours">{fNum(estPhaseHourTotals[phase.code], 1)}<Text style={unit}> h</Text></Text>
                            : <Text style={slot(78)}></Text>}
                        {etcRate > 0
                            ? <Text style={slot(62, { fontSize: 10, color: '#1e40af', fontWeight: '600' })} title="Estimate to complete, $ per hour">{fNum(etcRate, 2)}<Text style={unit}>/h</Text></Text>
                            : <Text style={slot(62)}></Text>}
                        {actRate > 0
                            ? <Text style={slot(62, { fontSize: 10, color: '#475569', fontWeight: '600' })} title="Actual $ per hour">{fNum(actRate, 2)}<Text style={unit}>/h</Text></Text>
                            : <Text style={slot(62)}></Text>}
                        {totalQty > 0
                            ? <Text style={slot(48, { fontSize: 11, color: isOverWithRemaining ? '#dc2626' : pct >= 100 ? '#16a34a' : '#475569', fontWeight: '600' })} title="Installed, percent of quantity">{fNum(pct, 1)}%</Text>
                            : <Text style={slot(48)}></Text>}
                    </>
                })()}
                            </Pressable>
                            {(expandedWeeks ? projectWeeks : visibleWeeks).map((week, wIdx) => {
                                const w = userData[phase.code + '=' + week]
                                const weekCost = w && moment(week).isAfter(moment(versionInfo.Version_Start_DateTime).startOf('week')) ? pNum(w.Number_Hours) * pNum(w.Rate) * pNum(w.Number_Units) : 0
                                const actualCost = actualPhaseWeekDollars[phase.code]?.[week] || 0
                                const displayValue = actualCost > 0 ? fNum(actualCost, 1) : weekCost > 0 ? fNum(weekCost, 1) : ''
                                const isActual = actualCost > 0
                                return <View key={wIdx} style={{ borderRight: forecastWeek.isSame(moment(week), 'week') ? '2px solid #1e40af' : 'none' }}>
                                    <Text style={[s.rate, { height: 32, lineHeight: '26px', backgroundColor: isActual ? '#f9fafb' : weekCost > 0 ? '#f0f4ff' : 'transparent', color: isActual ? '#475569' : weekCost > 0 ? '#1e40af' : '#94a3b8', fontWeight: displayValue ? '600' : 'normal' }]}>{displayValue}</Text>
                                </View>
                            })}
                        </View>}
                        {!isCollapsed && <PhaseRow phase={phase} heetData={isLiveVersion ? (heetPhaseWeeklyData[phase?.code] || {}) : {}} onCollapse={() => togglePhaseCollapse(phase.code)} />}
                    </View>
                    )
                })}
                </>}
            {uploadPreview && uploadPreview !== 'reading' && (
                <BurnToolPhasedUploadPreview
                    fileName={uploadPreview.fileName}
                    result={uploadPreview.result}
                    lockedVersion={lockedVersion}
                    applying={uploadApplying}
                    onApply={applyUpload}
                    onCancel={() => !uploadApplying && setUploadPreview(false)}
                />
            )}
            <AuditHistoryPanel tabKey="labor" />
            </View>
    );
};

export default BurnToolPhased;

